import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import { List, RowComponentProps, useListRef } from 'react-window';
import {useLocation } from 'react-router-dom'
import PropTypes from 'prop-types';
import {createPortal} from 'react-dom';
import { getAuth } from 'firebase/auth'; // FIXME:: get rid of this dependency
import {
  Announcements,
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragOverlay,
  DragMoveEvent,
  DragEndEvent,
  DragOverEvent,
  MeasuringStrategy,
  DropAnimation,
  Modifier,
  defaultDropAnimation,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import {
  buildTree,
  findItem,
  findItemDeep,
  flattenTree,  
  getProjection,
  getChildCount,
  removeItem,
  removeChildrenOf,
  setProperty,
  setPropertyOnTree,
  setGoalPropertyOnTree,
  flattenTreeAll,
} from './utilities.ts';
import saveTaskItemsToLocalStorage from '../dbUtils.js';
import type {FlattenedItem, SensorContext, TreeItem, TreeItems} from './types';
import {sortableTreeKeyboardCoordinates} from './keyboardCoordinates.ts';
import {SortableTreeItem} from './components/TreeItem/SortableTreeItem.tsx';
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import multiMonthPlugin from '@fullcalendar/multimonth'
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin, {ThirdPartyDraggable} from '@fullcalendar/interaction';
import RelatedNetwork from '../Views/Graph/Graph.tsx'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import {CSS} from '@dnd-kit/utilities';
import './SortableTree.css';
import MyButton from '../MyButtton.js';
import IconLabelTabs from '../IconTabs.js';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { debounce, useMediaQuery, useTheme } from '@mui/material';
import { Link as MaterialLink  } from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import { Link } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { ArrowDropDown, ArrowUpward, Clear, Close, Tune, UndoOutlined } from '@mui/icons-material';
import Reschedule from './components/Reschedule/Reschedule.tsx';
import { Tooltip } from '@mui/material';

const now = new Date()

const defaultInitialItems: TreeItems = [
  {
    id: 28,
    title: 'Home',
    children: [],
    start: new Date(2025, 3, 13, 15, 30, 0),
    end: new Date(2025, 3, 13, 16, 0, 0),
  },
  {
    id: 29,
    title: 'Test',
    children: [],
    start: new Date(new Date().setHours(new Date().getHours() - 3)),
    end: new Date(new Date().setHours(new Date().getHours() + 3)),
  },
  {
    id: 30,
    title: 'Collections',
    children: [
      {id: 'Spring', children: [], start: new Date, end: new Date},
      {id: 'Summer', children: []},
      {id: 'Fall', children: []},
      {id: 'Winter', children: []},
    ],
    start: new Date(new Date().setHours(new Date().getHours() - 2)),
    end: new Date(new Date().setHours(new Date().getHours() + 4)),
  },
  {
    id: 31,
    title: 'About Us',
    children: []
  },
  {
    id: 32,
    title: 'My Account',
    children: [
      {id: 'Addresses', children: []},
      {id: 'Order History', children: []},
    ],
  },
];

const measuring = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
};

const dropAnimationConfig: DropAnimation = {
  keyframes({transform}) {
    return [
      {opacity: 1, transform: CSS.Transform.toString(transform.initial)},
      {
        opacity: 0,
        transform: CSS.Transform.toString({
          ...transform.final,
          x: transform.final.x + 5,
          y: transform.final.y + 5,
        }),
      },
    ];
  },
  easing: 'ease-out',
  sideEffects({active}) {
    active.node.animate([{opacity: 0}, {opacity: 1}], {
      duration: defaultDropAnimation.duration,
      easing: defaultDropAnimation.easing,
    });
  },
};

const taskStatuses = [
  { id: 1, name: 'New' },
  { id: 2, name: 'Processing' },
  { id: 3, name: 'Ready' },
  { id: 4, name: 'Scheduled' },  
  { id: 5, name: 'In Progress' },  
  { id: 6, name: 'Completed' },
  { id: 7, name: 'Any/All' },
]

const primaryLifeAreas = [
  { id: 1, name: 'None' },
  { id: 2, name: 'Career' },
  { id: 3, name: 'Finances' },
  { id: 4, name: 'Health' },
  { id: 5, name: 'Interests' },
  { id: 6, name: 'Knowledge' },  
  { id: 7, name: 'Lifestyle' },
  { id: 8, name: 'Organisation' },
  { id: 9, name: 'Social' }, 
  { id: 10, name: 'Relationships' },
  { id: 11, name: 'Any/All' },
]

const taskTiers = [
  { id: 1, name: 'Any/All' },
  { id: 2, name: 'Tasks' },
  { id: 3, name: 'Goals' },    
]

const calendarViewModes = [
  { name: 'multiMonthYear' },
  { name: 'dayGridYear' },
  { name: 'dayGridMonth' },
  { name: 'timeGridWeek' },
  { name: 'timeGridDay' }, 
  { name: 'listMonth' },
  { name: 'listWeek' },
  { name: 'listDay' },
  { name: 'timeGridThreeDay' }
]

interface Props {
  collapsible?: boolean;
  items?: TreeItems;
  updateItems?(items: TreeItems): void;
  handleUndo?(): void;
  setSelectedItem?(item: FlattenedItem): void;
  indentationWidth?: number;
  initialTabValue?: number;
  indicator?: boolean;
  removable?: boolean;
  initialTaskStatusesIndex?: number;
  initialPrimaryLifeAreasIndex?: number;
  initialTaskTiersIndex?: number;
  initialEarliestEstimatedCommencementDateTime?: string;
  initialLatestEstimatedCommencementDateTime?: string;
  planning?: string;
  home?: false
}

export function SortableTree({
  collapsible,
  items = defaultInitialItems,
  updateItems,
  handleUndo,
  setSelectedItem,
  indicator = false,
  indentationWidth = 50,
  initialTabValue = 0,
  removable,
  initialTaskStatusesIndex: initialTaskStatusesIndex = 6,
  initialPrimaryLifeAreasIndex: initialPrimaryLifeAreasIndex = 10,
  initialTaskTiersIndex: initialTaskTiersIndex = 0,
  initialEarliestEstimatedCommencementDateTime: initialEarliestEstimatedCommencementDateTime = "",
  initialLatestEstimatedCommencementDateTime: initialLatestEstimatedCommencementDateTime = "",
  planning: planning = "",
  home: home = false
}: Props) {

  console.log("Week start date/Time: "+ initialEarliestEstimatedCommencementDateTime);
  console.log("Week start date/Time: "+ initialLatestEstimatedCommencementDateTime);

// Source - https://stackoverflow.com/a/74454857
// Posted by sleepystar96
// Retrieved 2026-03-24, License - CC BY-SA 4.0

// TODO: Fix this shite...
let initialEarliestEstimatedCommencementLocalISODateTime = "";

if (initialEarliestEstimatedCommencementDateTime !== "") {
  const initialEarliestEstimatedCommencementTimeMs = new Date(initialEarliestEstimatedCommencementDateTime).getTime();  
  const utcDate = new Date(initialEarliestEstimatedCommencementTimeMs)
  const localTime = initialEarliestEstimatedCommencementTimeMs - utcDate.getTimezoneOffset() * 60 * 1000
  const localDate = new Date(localTime)
  initialEarliestEstimatedCommencementLocalISODateTime = localDate.toISOString().replace("Z", "");
  console.log(utcDate.toISOString(), initialEarliestEstimatedCommencementLocalISODateTime);
}

let initialLatestEstimatedCommencementLocalISODateTime = "";

if (initialLatestEstimatedCommencementDateTime !== "") {
  const initialLatestEstimatedCommencementTimeMs = new Date(initialLatestEstimatedCommencementDateTime).getTime();
  const utcDate2 = new Date(initialLatestEstimatedCommencementTimeMs)
  const localTime2 = initialLatestEstimatedCommencementTimeMs - utcDate2.getTimezoneOffset() * 60 * 1000
  const localDate2 = new Date(localTime2)
  initialLatestEstimatedCommencementLocalISODateTime = localDate2.toISOString().replace("Z", "");
  console.log(utcDate2.toISOString(), initialLatestEstimatedCommencementLocalISODateTime);
}

  // const [items, setItems] = useState(initialItems);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeCalendarViewModeIndex, setActiveCalendarViewModeIndex] = useState<number>(3);
  const [activePlanningCalendarViewModeIndex, setActivePlanningCalendarViewModeIndex] = useState<number>(1);
  const [activeCalendarViewDisplayedDateRangeStartDateTime, setActiveCalendarViewDisplayedDateRangeStartDateTime] 
        = useState<string>(new Date(Date.now()).toISOString());

  const [focusToTreeItemId, setFocusToTreeItemId] = useState<UniqueIdentifier | null>(null); 
  const focusToTreeItemIdRef = useRef<UniqueIdentifier|null>(null);

  const [overId, setOverId] = useState<UniqueIdentifier | null>(null);
  const [offsetLeft, setOffsetLeft] = useState(0);  
  const [currentPosition, setCurrentPosition] = useState<{
    parentId: UniqueIdentifier | null;
    overId: UniqueIdentifier;
  } | null>(null);

  const [expandedIdsMap, setExpandedIds] = React.useState<Record<UniqueIdentifier,boolean>>({});

  const [tabValue, setTabValue] = React.useState(initialTabValue);
  // const [selectedState, setSelectedState] = useState(initialState ? initialState: states[0]);
  // const [selectedPrimaryLabel, setSelectedPrimaryLabel] = useState(initialPrimaryLabel ? initialPrimaryLabel: primaryLabels[0]);
  const [selectedTaskStatus, setSelectedTaskStatus] = useState(taskStatuses[initialTaskStatusesIndex]);
  const [selectedPrimaryLifeArea, setSelectedPrimaryLifeArea] = useState(primaryLifeAreas[initialPrimaryLifeAreasIndex]);
  const [selectedTaskTier, setSelectedTaskTier] = useState(taskTiers[initialTaskTiersIndex]);
  const [selectedEarliestCreationDateTime, setSelectedEarliestCreationDate] = useState("");
  const [selectedEarliestEstimatedCommencementDateTime, setSelectedEarliestEstimatedCommencementDateTime] = useState<string>(initialEarliestEstimatedCommencementLocalISODateTime);
  const [selectedLatestEstimatedCommencementDateTime, setSelectedLatestEstimatedCommencementDateTime] = useState<string>(initialLatestEstimatedCommencementLocalISODateTime);
  const [selectedEarliestPlannedDateTime, setSelectedEarliestPlannedDateTime] = useState<string>(initialEarliestEstimatedCommencementLocalISODateTime);
  const [selectedLatestPlannedDateTime, setSelectedLatestPlannedDateTime] = useState<string>(initialLatestEstimatedCommencementLocalISODateTime);
  const [selectedEarliestScheduledDateTime, setSelectedEarliestScheduledDateTime] = useState<string>(initialEarliestEstimatedCommencementLocalISODateTime);
  const [selectedLatestScheduledDateTime, setSelectedLatestScheduledDateTime] = useState<string>(initialLatestEstimatedCommencementLocalISODateTime);
  const [selectedNavigationTaskId, setSelectedNavigationTaskId] = useState<UniqueIdentifier | null>(null);
  const [selectedNavigationTaskParentFlattenedItem, setSelectedNavigationTaskParentFlattenedItem] = useState<FlattenedItem | null>(null);
  const [isPlanningMode, setIsPlanningMode] = useState<boolean>(planning === "" ? false : true );
  const [isDataConfigPanelHidden, setDataConfigPanelHidden] = useState<boolean>( true );
  const [pageTitle, setPageTitle] = React.useState(null);

  const titleFilter = createFilterOptions();

  const [headerText, setHeaderText] = React.useState("");

  const titleInputRef = useFocus();

  // TODO: The following seems super hacky... it'll work for now but there
  // must be a better prescribed way...
  // TODO: Yup.  I will in fact have to wrap each current 'SortableTree' 
  // component becaues I need to change/set the 'page' heading anyway.
  // Put another way, each filter/view combo actually is it's own
  //  page/panel.
  let location = useLocation()

  useEffect(
  ()=>{
    const newHeaderText = location.pathname.slice(1).replace("/", ": ").replace(/ .*?\//, " ").replace(/.*?\//, "").replace(/_/g, " ");

  setHeaderText(newHeaderText);
  setPageTitle({ 
                title: newHeaderText,
              });
  setSelectedEarliestCreationDate("");

  setSelectedEarliestEstimatedCommencementDateTime("");
  setSelectedLatestEstimatedCommencementDateTime("");

  setSelectedEarliestPlannedDateTime("");
  setSelectedLatestPlannedDateTime("");
  
  setSelectedEarliestScheduledDateTime("");
  setSelectedLatestScheduledDateTime("");

  // TODO: Most of thiis should be located in the parent component with state passed in to this one.
  if (location.pathname === "/Home") {

    const startOfWeekDateTime = new Date(now.getDate()-7);  
    const endOfWeekDateTime = new Date(Date.now());
    endOfWeekDateTime.setDate(startOfWeekDateTime.getDate()+14);

    const startOfWeekDateTimeMs = new Date(startOfWeekDateTime).getTime();  
    const utcDate = new Date(startOfWeekDateTimeMs);
    const localTime = startOfWeekDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(localTime);

    const endOfWeekDateTimeMs = new Date(endOfWeekDateTime).getTime();
    const localTime2 = endOfWeekDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate2 = new Date(localTime2);

    // setSelectedEarliestEstimatedCommencementDateTime(localDate.toISOString().replace("Z", ""));
    // setSelectedEarliestScheduledDateTime(localDate.toISOString().replace("Z", ""));
    // setSelectedLatestEstimatedCommencementDateTime(localDate2.toISOString().replace("Z", ""));

    // setSelectedEarliestPlannedDateTime(localDate.toISOString().replace("Z", ""));
    // setSelectedLatestPlannedDateTime(localDate2.toISOString().replace("Z", ""));
    
    setHeaderText("Good Day, Michael!")
    setIsPlanningMode(false);
  }

  if (location.pathname === "/Schedule") {
    if (selectedTaskStatus != taskStatuses[6]) { 
      setSelectedTaskStatus(taskStatuses[6]);
    }
    if (selectedPrimaryLifeArea != primaryLifeAreas[10]) { 
      setSelectedPrimaryLifeArea(primaryLifeAreas[10]);
    }
    if (selectedTaskTier != taskTiers[0]) { 
      setSelectedTaskTier(taskTiers[0]);
    }
    setIsPlanningMode(false);
    setTabValue(4);
  }
  if (location.pathname === "/Scheduling/This_Week") {
    // if (selectedTaskStatus != taskStatuses[6]) { 
    //   setSelectedTaskStatus(taskStatuses[6]);
    // }
    // if (selectedPrimaryLifeArea != primaryLifeAreas[10]) { 
    //   setSelectedPrimaryLifeArea(primaryLifeAreas[10]);
    // }
    // if (selectedTaskTier != taskTiers[0]) { 
    //   setSelectedTaskTier(taskTiers[0]);
    // }

    //TODO: Adjust this to be the actual currnet calendar week and not just 7 days hence.
    const startOfWeekDateTime = new Date(Date.now());  
    const endOfWeekDateTime = new Date(Date.now());
    endOfWeekDateTime.setDate(startOfWeekDateTime.getDate()+7);

    const startOfWeekDateTimeMs = new Date(startOfWeekDateTime).getTime();  
    const utcDate = new Date(startOfWeekDateTimeMs);
    const localTime = startOfWeekDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(localTime);

    const endOfWeekDateTimeMs = new Date(endOfWeekDateTime).getTime();
    const localTime2 = endOfWeekDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate2 = new Date(localTime2);

    setSelectedEarliestEstimatedCommencementDateTime(localDate.toISOString().replace("Z", ""));
    setSelectedLatestEstimatedCommencementDateTime(localDate2.toISOString().replace("Z", ""));

    setIsPlanningMode(false);
    setTabValue(3);
  }

  if (location.pathname === "/Planning/Yearly") {
    // if (selectedTaskStatus != taskStatuses[6]) { 
    //   setSelectedTaskStatus(taskStatuses[6]);
    // }
    // if (selectedPrimaryLifeArea != primaryLifeAreas[10]) { 
    //   setSelectedPrimaryLifeArea(primaryLifeAreas[10]);
    // }
    // if (selectedTaskTier != taskTiers[0]) { 
    //   setSelectedTaskTier(taskTiers[0]);
    // }
    setIsPlanningMode(true);
    setActivePlanningCalendarViewModeIndex(1);
    setTabValue(3);
  }

  if (location.pathname === "/Goals") {
    if (selectedTaskStatus != taskStatuses[6]) { 
      setSelectedTaskStatus(taskStatuses[6]);
    }
    if (selectedPrimaryLifeArea != primaryLifeAreas[10]) { 
      setSelectedPrimaryLifeArea(primaryLifeAreas[10]);
    }
    if (selectedTaskTier != taskTiers[2]) { 
      setSelectedTaskTier(taskTiers[2]);
    }
    setIsPlanningMode(false);
    setTabValue(0);
  }

  if (location.pathname === "/Tasks") {
    if (selectedTaskStatus != taskStatuses[6]) { 
      setSelectedTaskStatus(taskStatuses[6]);
    }
    if (selectedPrimaryLifeArea != primaryLifeAreas[10]) { 
      setSelectedPrimaryLifeArea(primaryLifeAreas[10]);
    }
    if (selectedTaskTier != taskTiers[0]) { 
      setSelectedTaskTier(taskTiers[0]);
    }
    setIsPlanningMode(false);
    setTabValue(0);
  }

  if (location.pathname === "/Tasks/New") {
    if (selectedTaskStatus != taskStatuses[0]) { 
      setSelectedTaskStatus(taskStatuses[0]);
    }
    if (selectedPrimaryLifeArea != primaryLifeAreas[10]) { 
      setSelectedPrimaryLifeArea(primaryLifeAreas[10]);
    }
    if (selectedTaskTier != taskTiers[0]) { 
      setSelectedTaskTier(taskTiers[0]);
    }
    setIsPlanningMode(false);
    setTabValue(0);
  }

  if (location.pathname === "/Tasks/Processing") {
    if (selectedTaskStatus != taskStatuses[1]) { 
      setSelectedTaskStatus(taskStatuses[1]);
    }
    if (selectedPrimaryLifeArea != primaryLifeAreas[10]) { 
      setSelectedPrimaryLifeArea(primaryLifeAreas[10]);
    }
    if (selectedTaskTier != taskTiers[0]) { 
      setSelectedTaskTier(taskTiers[0]);
    }
    setIsPlanningMode(false);
    setTabValue(0);
  }

  if (location.pathname === "/Tasks/Ready") {
    if (selectedTaskStatus != taskStatuses[2]) { 
      setSelectedTaskStatus(taskStatuses[2]);
    }
    if (selectedPrimaryLifeArea != primaryLifeAreas[10]) { 
      setSelectedPrimaryLifeArea(primaryLifeAreas[10]);
    }
    if (selectedTaskTier != taskTiers[0]) { 
      setSelectedTaskTier(taskTiers[0]);
    }
    setIsPlanningMode(false);
    setTabValue(0);
  }

  if (location.pathname === "/Tasks/Scheduled") {
    if (selectedTaskStatus != taskStatuses[3]) { 
      setSelectedTaskStatus(taskStatuses[3]);
    }
    if (selectedPrimaryLifeArea != primaryLifeAreas[10]) { 
      setSelectedPrimaryLifeArea(primaryLifeAreas[10]);
    }
    if (selectedTaskTier != taskTiers[0]) { 
      setSelectedTaskTier(taskTiers[0]);
    }
    setIsPlanningMode(false);
    setTabValue(0);
  }

  if (location.pathname === "/Tasks/In_Progress") {
    if (selectedTaskStatus != taskStatuses[4]) { 
      setSelectedTaskStatus(taskStatuses[4]);
    }
    if (selectedPrimaryLifeArea != primaryLifeAreas[10]) { 
      setSelectedPrimaryLifeArea(primaryLifeAreas[10]);
    }
    if (selectedTaskTier != taskTiers[0]) { 
      setSelectedTaskTier(taskTiers[0]);
    }
    setIsPlanningMode(false);
    setTabValue(0);
  }

  if (location.pathname === "/Tasks/Recent") {
    if (selectedTaskStatus != taskStatuses[6]) { 
      setSelectedTaskStatus(taskStatuses[6]);
    }
    if (selectedPrimaryLifeArea != primaryLifeAreas[10]) { 
      setSelectedPrimaryLifeArea(primaryLifeAreas[10]);
    }
    if (selectedTaskTier != taskTiers[0]) { 
      setSelectedTaskTier(taskTiers[0]);
    }

    const oneWeekAgoDateTime = new Date(Date.now());
    oneWeekAgoDateTime.setDate(new Date(Date.now()).getDate()-7);

    setSelectedEarliestCreationDate(oneWeekAgoDateTime.toISOString().replace("Z", ""));

    setIsPlanningMode(false);
    setTabValue(0);
  }
  
  if (location.pathname === "/Planning/Weekly" ||
      location.pathname === "/Planning/Weekly/This_Week" ||
      location.pathname === "/Planning/Weekly/Next_Week" ||
      location.pathname === "/Planning/Weekly/Week-Hence" 
      ) {

    setIsPlanningMode(true);
    setActivePlanningCalendarViewModeIndex(3);
    setTabValue(3);
  }

  if (location.pathname === "/Planning/Monthly" ||
      location.pathname === "/Planning/Monthly/This_Month" ||
      location.pathname === "/Planning/Monthly/Next_Month" ||
      location.pathname === "/Planning/Monthly/Month-Hence" 
      ) {

    setIsPlanningMode(true);
    setActivePlanningCalendarViewModeIndex(2);
    setTabValue(3);
  }
  
  if (location.pathname === "/Planning/Quarterly" ||
      location.pathname === "/Planning/Quarterly/This_Quarter" ||
      location.pathname === "/Planning/Quarterly/Next_Quarter" ||
      location.pathname === "/Planning/Quarterly/Quarter-Hence" 
      ) {

    setIsPlanningMode(true);
    setActivePlanningCalendarViewModeIndex(0);
    setTabValue(3);
  }

  if (location.pathname === "/Planning/Yearly" ||
      location.pathname === "/Planning/Yearly/This_Year" ||
      location.pathname === "/Planning/Yearly/Next_Year" ||
      location.pathname === "/Planning/Yearly/Year-Hence" 
    ) {

    setIsPlanningMode(true);
    setActivePlanningCalendarViewModeIndex(1);
    setTabValue(3);
  }
  
  if (location.pathname === "/Scheduling/Today") {

    //TODO: Refactor out with other cases.
    const startOfDailySchedulingPeriodDateTime = new Date(Date.now());  
    const endOfDailySchedulingPeriodDateTime = new Date(Date.now());

    endOfDailySchedulingPeriodDateTime.setDate(startOfDailySchedulingPeriodDateTime.getDate()+1);
    endOfDailySchedulingPeriodDateTime.setHours(12,0,0,0);
    startOfDailySchedulingPeriodDateTime.setDate(startOfDailySchedulingPeriodDateTime.getDate()-1);
    startOfDailySchedulingPeriodDateTime.setHours(12,0,0,0);

    const startOfDailySchedulingPeriodDateTimeMs = new Date(startOfDailySchedulingPeriodDateTime).getTime();  
    const utcDate = new Date(startOfDailySchedulingPeriodDateTimeMs);
    const localTime = startOfDailySchedulingPeriodDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(localTime);

    const endOfWeekDateTimeMs = new Date(endOfDailySchedulingPeriodDateTime).getTime();
    const localTime2 = endOfWeekDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate2 = new Date(localTime2);

    setSelectedEarliestPlannedDateTime(localDate.toISOString().replace("Z", ""));
    setSelectedLatestPlannedDateTime(localDate2.toISOString().replace("Z", ""));

    setIsPlanningMode(false);
    setActivePlanningCalendarViewModeIndex(4);
    setTabValue(3);
  }

  if (location.pathname === "/Scheduling/Tomorrow") {

    const startOfDailySchedulingPeriodDateTime = new Date(Date.now());  
    const endOfDailySchedulingPeriodDateTime = new Date(Date.now());

    endOfDailySchedulingPeriodDateTime.setDate(startOfDailySchedulingPeriodDateTime.getDate()+2);
    endOfDailySchedulingPeriodDateTime.setHours(12,0,0,0);
    startOfDailySchedulingPeriodDateTime.setDate(startOfDailySchedulingPeriodDateTime.getDate());
    startOfDailySchedulingPeriodDateTime.setHours(12,0,0,0);

    const startOfDailySchedulingPeriodDateTimeMs = new Date(startOfDailySchedulingPeriodDateTime).getTime();  
    const utcDate = new Date(startOfDailySchedulingPeriodDateTimeMs);
    const localTime = startOfDailySchedulingPeriodDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(localTime);

    const endOfWeekDateTimeMs = new Date(endOfDailySchedulingPeriodDateTime).getTime();
    const localTime2 = endOfWeekDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate2 = new Date(localTime2);

    setSelectedEarliestPlannedDateTime(localDate.toISOString().replace("Z", ""));
    setSelectedLatestPlannedDateTime(localDate2.toISOString().replace("Z", ""));

    setIsPlanningMode(false);
    setActivePlanningCalendarViewModeIndex(3);
    setTabValue(3);
  }

  if (location.pathname === "/Scheduling/Next_Three_Days") {

    const startOfDailySchedulingPeriodDateTime = new Date(Date.now());  
    const endOfDailySchedulingPeriodDateTime = new Date(Date.now());

    endOfDailySchedulingPeriodDateTime.setDate(startOfDailySchedulingPeriodDateTime.getDate()+3);
    endOfDailySchedulingPeriodDateTime.setHours(12,0,0,0);
    startOfDailySchedulingPeriodDateTime.setDate(startOfDailySchedulingPeriodDateTime.getDate()-1);
    startOfDailySchedulingPeriodDateTime.setHours(12,0,0,0);

    const startOfDailySchedulingPeriodDateTimeMs = new Date(startOfDailySchedulingPeriodDateTime).getTime();  
    const utcDate = new Date(startOfDailySchedulingPeriodDateTimeMs);
    const localTime = startOfDailySchedulingPeriodDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(localTime);

    const endOfWeekDateTimeMs = new Date(endOfDailySchedulingPeriodDateTime).getTime();
    const localTime2 = endOfWeekDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate2 = new Date(localTime2);

    setSelectedEarliestPlannedDateTime(localDate.toISOString().replace("Z", ""));
    setSelectedLatestPlannedDateTime(localDate2.toISOString().replace("Z", ""));

    setIsPlanningMode(false);
    setActivePlanningCalendarViewModeIndex(3);
    setTabValue(3);
  }

  if (location.pathname === "/Scheduling/This_Week") {

    const currentDayOfWeek = new Date(Date.now()).getDay();
    const daysToSubtract = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;
    const startOfWeeklySchedulingPeriodDateTime = new Date(Date.now());  
    const endOfWeeklySchedulingPeriodDateTime = new Date(Date.now());

    startOfWeeklySchedulingPeriodDateTime.setDate(startOfWeeklySchedulingPeriodDateTime.getDate()-daysToSubtract)
    endOfWeeklySchedulingPeriodDateTime.setDate(startOfWeeklySchedulingPeriodDateTime.getDate()+8);
    endOfWeeklySchedulingPeriodDateTime.setHours(0,0,0,0);
    startOfWeeklySchedulingPeriodDateTime.setDate(startOfWeeklySchedulingPeriodDateTime.getDate()-1);
    startOfWeeklySchedulingPeriodDateTime.setHours(0,0,0,0);

    const startOfWeekDateTimeMs = new Date(startOfWeeklySchedulingPeriodDateTime).getTime();  
    const utcDate = new Date(startOfWeekDateTimeMs);
    const localTime = startOfWeekDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(localTime);

    const endOfWeekDateTimeMs = new Date(endOfWeeklySchedulingPeriodDateTime).getTime();
    const localTime2 = endOfWeekDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate2 = new Date(localTime2);

    setSelectedEarliestPlannedDateTime(localDate.toISOString().replace("Z", ""));
    setSelectedLatestPlannedDateTime(localDate2.toISOString().replace("Z", ""));
    
    setIsPlanningMode(false);
    setActivePlanningCalendarViewModeIndex(3);
    setTabValue(3);
  }

    if (location.pathname === "/Scheduling/Next_Week") {

    const currentDayOfWeek = new Date(Date.now()).getDay();
    const daysToSubtract = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;
    const startOfWeeklySchedulingPeriodDateTime = new Date(Date.now());  
    const endOfWeeklySchedulingPeriodDateTime = new Date(Date.now());

    startOfWeeklySchedulingPeriodDateTime.setDate(startOfWeeklySchedulingPeriodDateTime.getDate()-daysToSubtract)
    endOfWeeklySchedulingPeriodDateTime.setDate(startOfWeeklySchedulingPeriodDateTime.getDate()+15);
    endOfWeeklySchedulingPeriodDateTime.setHours(0,0,0,0);
    startOfWeeklySchedulingPeriodDateTime.setDate(startOfWeeklySchedulingPeriodDateTime.getDate()+6);
    startOfWeeklySchedulingPeriodDateTime.setHours(0,0,0,0);

    const startOfWeekDateTimeMs = new Date(startOfWeeklySchedulingPeriodDateTime).getTime();  
    const utcDate = new Date(startOfWeekDateTimeMs);
    const localTime = startOfWeekDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(localTime);

    const endOfWeekDateTimeMs = new Date(endOfWeeklySchedulingPeriodDateTime).getTime();
    const localTime2 = endOfWeekDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate2 = new Date(localTime2);

    setSelectedEarliestPlannedDateTime(localDate.toISOString().replace("Z", ""));
    setSelectedLatestPlannedDateTime(localDate2.toISOString().replace("Z", ""));
    
    setIsPlanningMode(false);
    setActivePlanningCalendarViewModeIndex(2);
    setTabValue(3);
  }

  if (location.pathname === "/Scheduling/Week-Hence") {

    const startOfWeeklySchedulingPeriodDateTime = new Date(Date.now());  
    const endOfWeeklySchedulingPeriodDateTime = new Date(Date.now());
    endOfWeeklySchedulingPeriodDateTime.setDate(startOfWeeklySchedulingPeriodDateTime.getDate()+8);
    endOfWeeklySchedulingPeriodDateTime.setHours(0,0,0,0);
    startOfWeeklySchedulingPeriodDateTime.setDate(startOfWeeklySchedulingPeriodDateTime.getDate()-1);
    startOfWeeklySchedulingPeriodDateTime.setHours(0,0,0,0);

    const startOfWeeklySchedulingPeriodDateTimeMs = new Date(startOfWeeklySchedulingPeriodDateTime).getTime();  
    const utcDate = new Date(startOfWeeklySchedulingPeriodDateTimeMs);
    const localTime = startOfWeeklySchedulingPeriodDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(localTime);

    const endOfWeekDateTimeMs = new Date(endOfWeeklySchedulingPeriodDateTime).getTime();
    const localTime2 = endOfWeekDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate2 = new Date(localTime2);

    setSelectedEarliestPlannedDateTime(localDate.toISOString().replace("Z", ""));
    setSelectedLatestPlannedDateTime(localDate2.toISOString().replace("Z", ""));

    setIsPlanningMode(false);
    setTabValue(3);
  }

  if (location.pathname === "/Scheduling/This_Month") {

    const startOfMonthlySchedulingPeriodDateTime = new Date(Date.now());  
    const endOfMonthlySchedulingPeriodDateTime = new Date(Date.now());

    startOfMonthlySchedulingPeriodDateTime.setDate(1);
    endOfMonthlySchedulingPeriodDateTime.setHours(0,0,0,0);
    endOfMonthlySchedulingPeriodDateTime.setMonth(startOfMonthlySchedulingPeriodDateTime.getMonth()+1);
    endOfMonthlySchedulingPeriodDateTime.setDate(8);
    startOfMonthlySchedulingPeriodDateTime.setHours(0,0,0,0);
    startOfMonthlySchedulingPeriodDateTime.setDate(-6);

    const startOfMonthDateTimeMs = new Date(startOfMonthlySchedulingPeriodDateTime).getTime();  
    const utcDate = new Date(startOfMonthDateTimeMs);
    const localTime = startOfMonthDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(localTime);

    const endOfMonthDateTimeMs = new Date(endOfMonthlySchedulingPeriodDateTime).getTime();
    const localTime2 = endOfMonthDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate2 = new Date(localTime2);

    setSelectedEarliestPlannedDateTime(localDate.toISOString().replace("Z", ""));
    setSelectedLatestPlannedDateTime(localDate2.toISOString().replace("Z", ""));

    setIsPlanningMode(false);
    setActivePlanningCalendarViewModeIndex(2);
    setTabValue(3);
  }

  if (location.pathname === "/Scheduling/Month-Hence") {

    const startOfMonthlySchedulingPeriodDateTime = new Date(Date.now());  
    const endOfMonthlySchedulingPeriodDateTime = new Date(Date.now());

    endOfMonthlySchedulingPeriodDateTime.setHours(0,0,0,0);
    endOfMonthlySchedulingPeriodDateTime.setMonth(startOfMonthlySchedulingPeriodDateTime.getMonth()+1);
    endOfMonthlySchedulingPeriodDateTime.setDate(endOfMonthlySchedulingPeriodDateTime.getDate()+8);
    startOfMonthlySchedulingPeriodDateTime.setHours(0,0,0,0);
    startOfMonthlySchedulingPeriodDateTime.setDate(-6);

    const startOfMonthDateTimeMs = new Date(startOfMonthlySchedulingPeriodDateTime).getTime();  
    const utcDate = new Date(startOfMonthDateTimeMs);
    const localTime = startOfMonthDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(localTime);

    const endOfMonthDateTimeMs = new Date(endOfMonthlySchedulingPeriodDateTime).getTime();
    const localTime2 = endOfMonthDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate2 = new Date(localTime2);

    setSelectedEarliestPlannedDateTime(localDate.toISOString().replace("Z", ""));
    setSelectedLatestPlannedDateTime(localDate2.toISOString().replace("Z", ""));

    setIsPlanningMode(false);
    setActivePlanningCalendarViewModeIndex(2);
    setTabValue(3);
  }
  
  if (location.pathname === "/Review/Daily") {

    const startOfDailyReviewPeriodDateTime = new Date(Date.now());  
    const endOfDailyReviewPeriodDateTime = new Date(Date.now());

    endOfDailyReviewPeriodDateTime.setDate(startOfDailyReviewPeriodDateTime.getDate()+1);
    endOfDailyReviewPeriodDateTime.setHours(12,0,0,0);
    startOfDailyReviewPeriodDateTime.setDate(startOfDailyReviewPeriodDateTime.getDate()-1);
    startOfDailyReviewPeriodDateTime.setHours(12,0,0,0);

    const startOfDailyReviewPeriodDateTimeMs = new Date(startOfDailyReviewPeriodDateTime).getTime();  
    const utcDate = new Date(startOfDailyReviewPeriodDateTimeMs);
    const localTime = startOfDailyReviewPeriodDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(localTime);

    const endOfWeekDateTimeMs = new Date(endOfDailyReviewPeriodDateTime).getTime();
    const localTime2 = endOfWeekDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate2 = new Date(localTime2);

    setSelectedEarliestPlannedDateTime(localDate.toISOString().replace("Z", ""));
    setSelectedLatestPlannedDateTime(localDate2.toISOString().replace("Z", ""));

    setIsPlanningMode(false);
    setTabValue(0);
  }
  
  if (location.pathname === "/Review/Weekly") {

    const currentDayOfWeek = new Date(Date.now()).getDay();
    const daysToSubtract = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;
    const startOfWeeklyReviewPeriodDateTime = new Date(Date.now());  
    const endOfWeeklyReviewPeriodDateTime = new Date(Date.now());

    startOfWeeklyReviewPeriodDateTime.setDate(startOfWeeklyReviewPeriodDateTime.getDate()-daysToSubtract)
    endOfWeeklyReviewPeriodDateTime.setDate(startOfWeeklyReviewPeriodDateTime.getDate()+8);
    endOfWeeklyReviewPeriodDateTime.setHours(0,0,0,0);
    startOfWeeklyReviewPeriodDateTime.setDate(startOfWeeklyReviewPeriodDateTime.getDate()-1);
    startOfWeeklyReviewPeriodDateTime.setHours(0,0,0,0);

    const startOfWeekDateTimeMs = new Date(startOfWeeklyReviewPeriodDateTime).getTime();  
    const utcDate = new Date(startOfWeekDateTimeMs);
    const localTime = startOfWeekDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(localTime);

    const endOfWeekDateTimeMs = new Date(endOfWeeklyReviewPeriodDateTime).getTime();
    const localTime2 = endOfWeekDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate2 = new Date(localTime2);

    setSelectedEarliestPlannedDateTime(localDate.toISOString().replace("Z", ""));
    setSelectedLatestPlannedDateTime(localDate2.toISOString().replace("Z", ""));

    setIsPlanningMode(false);
    setTabValue(0);
  }

  if (location.pathname === "/Review/Monthly") {

    const startOfMonthlyReviewPeriodDateTime = new Date(Date.now());  
    const endOfMonthlyReviewPeriodDateTime = new Date(Date.now());
    
    startOfMonthlyReviewPeriodDateTime.setDate(1);
    endOfMonthlyReviewPeriodDateTime.setHours(0,0,0,0);
    endOfMonthlyReviewPeriodDateTime.setMonth(startOfMonthlyReviewPeriodDateTime.getMonth()+1);
    endOfMonthlyReviewPeriodDateTime.setDate(8);
    startOfMonthlyReviewPeriodDateTime.setHours(0,0,0,0);
    startOfMonthlyReviewPeriodDateTime.setDate(-6);

    const startOfMonthDateTimeMs = new Date(startOfMonthlyReviewPeriodDateTime).getTime();  
    const utcDate = new Date(startOfMonthDateTimeMs);
    const localTime = startOfMonthDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(localTime);

    const endOfMonthDateTimeMs = new Date(endOfMonthlyReviewPeriodDateTime).getTime();
    const localTime2 = endOfMonthDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate2 = new Date(localTime2);

    setSelectedEarliestPlannedDateTime(localDate.toISOString().replace("Z", ""));
    setSelectedLatestPlannedDateTime(localDate2.toISOString().replace("Z", ""));

    setIsPlanningMode(false);
    setTabValue(0);
  }

  if (location.pathname === "/Review/Quarterly") {

    const startOfQuarterlyReviewPeriodDateTime = new Date(Date.now());  
    const endOfQuarterlyReviewPeriodDateTime = new Date(Date.now());
    const currentMonth = startOfQuarterlyReviewPeriodDateTime.getMonth();
    
    let currentQuarterStartMonth = 1;
    let currentQuarterEndMonth =3;
    if( currentMonth === 0||currentMonth ===1||currentMonth ===2) {
      currentQuarterStartMonth = 0;
      currentQuarterEndMonth = 2;
    }
    else if( currentMonth === 3||currentMonth === 4||currentMonth === 5) {
      currentQuarterStartMonth = 3;
      currentQuarterEndMonth = 3;
    }
    else if( currentMonth === 6||currentMonth === 7||currentMonth === 8) {
      currentQuarterStartMonth = 6;
      currentQuarterEndMonth = 8;
    }
    else if( currentMonth === 9||currentMonth ===10||currentMonth ===11) {
      currentQuarterStartMonth = 9;
      currentQuarterEndMonth = 11;
    }

    startOfQuarterlyReviewPeriodDateTime.setDate(1);
    endOfQuarterlyReviewPeriodDateTime.setHours(0,0,0,0);
    endOfQuarterlyReviewPeriodDateTime.setMonth(currentQuarterEndMonth+1);
    endOfQuarterlyReviewPeriodDateTime.setDate(8);
    startOfQuarterlyReviewPeriodDateTime.setHours(0,0,0,0);
    startOfQuarterlyReviewPeriodDateTime.setMonth(currentQuarterStartMonth);
    startOfQuarterlyReviewPeriodDateTime.setDate(-6);

    const startOfQuarterDateTimeMs = new Date(startOfQuarterlyReviewPeriodDateTime).getTime();  
    const utcDate = new Date(startOfQuarterDateTimeMs);
    const localTime = startOfQuarterDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(localTime);

    const endOfQuarterDateTimeMs = new Date(endOfQuarterlyReviewPeriodDateTime).getTime();
    const localTime2 = endOfQuarterDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate2 = new Date(localTime2);

    setSelectedEarliestPlannedDateTime(localDate.toISOString().replace("Z", ""));
    setSelectedLatestPlannedDateTime(localDate2.toISOString().replace("Z", ""));

    setIsPlanningMode(false);
    setTabValue(0);
  }

  if (location.pathname === "/Review/Yearly") {

    const startOfYearlyReviewPeriodDateTime = new Date(Date.now());  
    const endOfYearlyReviewPeriodDateTime = new Date(Date.now());
    startOfYearlyReviewPeriodDateTime.setDate(1);
    endOfYearlyReviewPeriodDateTime.setHours(0,0,0,0);
    endOfYearlyReviewPeriodDateTime.setDate(1);
    endOfYearlyReviewPeriodDateTime.setMonth(13);
    startOfYearlyReviewPeriodDateTime.setHours(0,0,0,0);
    startOfYearlyReviewPeriodDateTime.setMonth(0);
    startOfYearlyReviewPeriodDateTime.setMonth(startOfYearlyReviewPeriodDateTime.getMonth()-1);

    const startOfYearDateTimeMs = new Date(startOfYearlyReviewPeriodDateTime).getTime();  
    const utcDate = new Date(startOfYearDateTimeMs);
    const localTime = startOfYearDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(localTime);

    const endOfYearDateTimeMs = new Date(endOfYearlyReviewPeriodDateTime).getTime();
    const localTime2 = endOfYearDateTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate2 = new Date(localTime2);

    setSelectedEarliestPlannedDateTime(localDate.toISOString().replace("Z", ""));
    setSelectedLatestPlannedDateTime(localDate2.toISOString().replace("Z", ""));

    setIsPlanningMode(false);
    setTabValue(0);
  }

  }
  ,[location]
  );

  const calendarRef = useRef(null);

  useEffect(()=>{
    // const calendarApi = calendarRef.current.getAPI();
    // calendarApi.scrollToTime();
  // document.querySelector(".fc-now-indicator")?.scrollIntoView({block: "center"});
},[])

  const filteredFlattenedItems = useMemo(() => {

    let prunedItems = items;

    if(selectedNavigationTaskId) {
      prunedItems = Array(findItemDeep(items, selectedNavigationTaskId));
      if(prunedItems[0] !== undefined) {
        
        console.log("Pruned items! : " + prunedItems[0].id);
      }
      else {
        prunedItems = items;
        console.log("Didn't prune items...");
      }
    }

    const filteredFlattenedTree = flattenTree(prunedItems).filter(

      (a) => {
        if(selectedTaskStatus && !(selectedTaskStatus.name === taskStatuses[6].name )) {
          console.log("Task state filter activating...");
          if(a.status === selectedTaskStatus.name) {
            console.log("Filtering for state...");
            return true;
          }
          if(!(a.status) && (selectedTaskStatus.name === taskStatuses[0].name )) {
            return true;
          }          
          else {
            return false;
          }

        }
        return true;

      }).filter(
        (a) => {
          if(selectedPrimaryLifeArea && !(selectedPrimaryLifeArea.name === primaryLifeAreas[10].name )) {
            console.log("Task primary label filter activating...");
            if(a.primaryLabel === selectedPrimaryLifeArea.name) {
              console.log("Filtering for primary label..."); 
              return true;               
            }
            if(!(a.primaryLabel) && (selectedPrimaryLifeArea.name === primaryLifeAreas[0].name)) {
              return true;
            }
            else {
              return false;
            }
          }
          else {
            return true;
          }
        }).filter(
          (a) => {
            if(selectedTaskTier && !(selectedTaskTier.name === taskTiers[0].name )) {
              console.log("Task tier filter activating...");
              if(selectedTaskTier.name === taskTiers[2].name && a.goal) {
                return true;
              }
              else {
                return false;
              }
            } 
            else {
              return true;
            }   
        }).filter(
          (a) => {
            if(selectedEarliestCreationDateTime !== "") {
              console.log("Task creation after date/time filter activating...");

              if(a.creationDate) {  
                if(new Date(a.creationDate).getTime() > new Date(selectedEarliestCreationDateTime).getTime()) {
                  return true;
                }
                else {
                  return false;
                }
              }
              else {
                return false;
              }
            } 
            else {
              return true;
            }   
        }).filter(
          (a) => {
            if(selectedEarliestEstimatedCommencementDateTime !== "") {
              console.log("Estimated commencement after date/time filter activating...");

              if(a.estimatedCommencementDate) {  
                if(new Date(a.estimatedCommencementDate).getTime() > new Date(selectedEarliestEstimatedCommencementDateTime).getTime()) {
                  return true;
                }
                else {
                  return false;
                }
              }
              else {
                return false;
              }
            } 
            else {
              return true;
            }   
        }).filter(
          (a) => {
            if(selectedLatestEstimatedCommencementDateTime !== "") {
              console.log("Estimated commencement before date/time filter activating...");

              //FIXME:: Total Hack.  Updated TreeItem in types (and elsewhere) to have correct props.
              if(a.latestPossibleCompletionDate) {  
                if(new Date(a.latestPossibleCompletionDate).getTime() < new Date(selectedLatestEstimatedCommencementDateTime).getTime()) {
                  return true;
                }
                else {
                  return false;
                }
              }
              else {
                return false;
              }
            } 
            else {
              return true;
            }   
        }).filter(
          (a) => {
            if(selectedEarliestPlannedDateTime !== "") {
              console.log("Planned after date/time filter activating...");

              if(a.estimatedCommencementDate || a.idealCompletionDate || a.latestPossibleCompletionDate) {  
                if(a.estimatedCommencementDate && (new Date(a.estimatedCommencementDate).getTime() >= new Date(selectedEarliestPlannedDateTime).getTime())) {
                  return true;
                }
                else if(a.idealCompletionDate && (new Date(a.idealCompletionDate).getTime() >= new Date(selectedEarliestPlannedDateTime).getTime())) {
                  return true;
                }
                else if(a.latestPossibleCompletionDate && (new Date(a.latestPossibleCompletionDate).getTime() >= new Date(selectedEarliestPlannedDateTime).getTime())) {
                  return true;
                }
                else {
                  return false;
                }
              }
              else {
                return false;
              }
            } 
            else {
              return true;
            }   
        }).filter(
          (a) => {
            if(selectedLatestPlannedDateTime !== "") {
              console.log("Planned after date/time filter activating...");

              if(a.estimatedCommencementDate || a.idealCompletionDate || a.latestPossibleCompletionDate) {  
                if(a.estimatedCommencementDate && (new Date(a.estimatedCommencementDate).getTime() <= new Date(selectedLatestPlannedDateTime).getTime())) {
                  return true;
                }
                else if(a.idealCompletionDate && (new Date(a.idealCompletionDate).getTime() <= new Date(selectedLatestPlannedDateTime).getTime())) {
                  return true;
                }
                else if(a.latestPossibleCompletionDate && (new Date(a.latestPossibleCompletionDate).getTime() <= new Date(selectedLatestPlannedDateTime).getTime())) {
                  return true;
                }
                else {
                  return false;
                }
              }
              else {
                return false;
              }
            } 
            else {
              return true;
            }   
        }).filter(
          (a) => {
            if(selectedEarliestScheduledDateTime !== "") {
              console.log("Scheduled after date/time filter activating...");

              if(a.idealCompletionDate || a.latestPossibleCompletionDate) {  
                if(a.idealCompletionDate && (new Date(a.idealCompletionDate).getTime() >= new Date(selectedEarliestScheduledDateTime).getTime())) {
                  return true;
                }
                else if(a.latestPossibleCompletionDate && (new Date(a.latestPossibleCompletionDate).getTime() >= new Date(selectedEarliestScheduledDateTime).getTime())) {
                  return true;
                }
                else {
                  return false;
                }
              }
              else {
                return false;
              }
            } 
            else {
              return true;
            }   
        }).filter(
          (a) => {
            if(selectedLatestScheduledDateTime !== "") {
              console.log("Scheduled after date/time filter activating...");

              if(a.idealCompletionDate || a.latestPossibleCompletionDate) {  
                if(a.idealCompletionDate && (new Date(a.idealCompletionDate).getTime() <= new Date(selectedLatestScheduledDateTime).getTime())) {
                  return true;
                }
                else if(a.latestPossibleCompletionDate && (new Date(a.latestPossibleCompletionDate).getTime() <= new Date(selectedLatestScheduledDateTime).getTime())) {
                  return true;
                }
                else {
                  return false;
                }
              }
              else {
                return false;
              }
            } 
            else {
              return true;
            }   
        });

    // const collapsedItems = filteredFlattenedTree.reduce<string[]>(
    //   (acc, {children, collapsed, id}) =>
    //     collapsed && children.length ? [...acc, id] : acc,
    //   []
    // );

    // return removeChildrenOf(
    // filteredFlattenedTree,
    // activeId ? [activeId, ...collapsedItems] : collapsedItems
    // );

    return filteredFlattenedTree;
  
  }, [selectedNavigationTaskId,
      activeId,
      items,
      selectedTaskTier,
      selectedPrimaryLifeArea,
      selectedTaskStatus,
      selectedEarliestCreationDateTime,
      selectedEarliestEstimatedCommencementDateTime,
      selectedLatestEstimatedCommencementDateTime,
      selectedEarliestPlannedDateTime,
      selectedLatestPlannedDateTime,
      selectedEarliestScheduledDateTime,
      selectedLatestScheduledDateTime,
      isPlanningMode ]);

    const flattenedItems = useMemo(() => {

    const flattenedTree = flattenTreeAll(items);

    // Remove this when all clear to use flattenTree above.
    const collapsedItems = flattenedTree.reduce<string[]>(
      (acc, {children, collapsed, id}) =>
        collapsed && children.length ? [...acc, id] : acc,
      []
    );

    return removeChildrenOf(
      flattenedTree,
      activeId ? [activeId, ...collapsedItems] : collapsedItems
    );
  }, [activeId, items]);

  const dateOrderedFlattenedItems = useMemo(() => {
  //   return flattenTree(items).sort((a,b) => {
      
  //       let firstDate = '0';
  //       if(a.idealCompletionDate) {
  //         firstDate = a.idealCompletionDate
  //       }
  //       else{
  //         return 1;
  //       };
  //       let secondDate = '0';
  //       if(b.idealCompletionDate) {
  //         secondDate = b.idealCompletionDate
  //       }
  //       else{
  //         return -1;
  //       };        
      
  //       const firstTime = new Date(firstDate).getTime();
  //       const secondTime = new Date(secondDate).getTime();

  //       const result = firstTime - secondTime;
  //       if(result==0){
  //         return -1;
  //       }

  //       return result;
  // })
  return filteredFlattenedItems;
  }, [activeId, filteredFlattenedItems]);

  const calendarFilteredFlattenedItems = useMemo(() => {

    if(isPlanningMode) {
      filteredFlattenedItems.forEach((flattenedItem) => {
          if(flattenedItem.estimatedCommencementDate) {
            // if(flattenedItem.startStr) {
            //   flattenedItem.startStr = "";
            // }
            flattenedItem.start = new Date(flattenedItem.estimatedCommencementDate);          
          }
          else {
            flattenedItem.start = undefined;
          };

          if(flattenedItem.latestPossibleCompletionDate) {
            // if(flattenedItem.endStr) {
            //   flattenedItem.endStr = "";
            // }
            flattenedItem.end = new Date(flattenedItem.latestPossibleCompletionDate);          
          }
          else {
            flattenedItem.end = undefined;
          };
        
          if(flattenedItem.allDay !== undefined) {
            flattenedItem.allDay = true;
          }
        }
      );

    }
    else {
      filteredFlattenedItems.forEach((flattenedItem) => {
          if(flattenedItem.idealCompletionDate) {
            // if(flattenedItem.start) {
            //   flattenedItem.start = undefined;
            // }
            flattenedItem.start = new Date(flattenedItem.idealCompletionDate);
          }
          else {
            flattenedItem.start = undefined;
          };

          if(flattenedItem.latestPossibleCompletionDate) {
            // if(flattenedItem.end) {
            //   flattenedItem.end = undefined;
            // }
            flattenedItem.end = new Date(flattenedItem.latestPossibleCompletionDate);
          }
          else {
            flattenedItem.end = undefined;
          };
                  
          if(flattenedItem.allDay !== undefined) {
            flattenedItem.allDay = false;
          }
        }
      );

    }

    return filteredFlattenedItems;
  
  }, [ filteredFlattenedItems, isPlanningMode ]);

  const inputRefs = useFocuses()

  const schedulerTreeDraggableContainerRef = useRef(null);

  useEffect(() => {
    const containerEl = schedulerTreeDraggableContainerRef.current;

    let dragMirrorEl = null;
    let longPressTimer = null;
    let autoScrollTimer = null;
    let startDragX = 0;
    let startDragY = 0;

    if(schedulerContainerRef.current && schedulerHorizontalScrollPostionRef.current) {
      schedulerContainerRef.current.scrollLeft = schedulerHorizontalScrollPostionRef.current
    }

    if(containerEl && tabValue === 3) {

      const fullCalendarDraggable = new ThirdPartyDraggable(containerEl, {
            itemSelector: '.fc-draggable-node', 
            mirrorSelector: '.task-drag-mirror', 

            eventData: function(eventEl) {
              return {
              create: false, 
              id: eventEl.dataset.id,
              title: eventEl.dataset.title,
              // duration: '01:00',
              // startTime: '09:00:00',
              // allDay: true
            };
          }
        });

      // ACTIVE X-AXIS AUTO-SCROLLER
      const handleEdgeAutoScroll = (currentClientX, currentClientY, originalEvent) => {
        if (!schedulerContainerRef?.current) return;
        
        const rect = schedulerContainerRef.current.getBoundingClientRect();
        const leftEdgeThreshold = 10; // Distance in pixels from the edge to trigger scrolling
        const rightEdgeThreshold = 120; // Distance in pixels from the edge to trigger scrolling
        const scrollSpeed = 12;   // Speed of the horizontal scroll interval
        
        // Calculate distances to left and right bounding limits
        const distanceToLeftEdge = currentClientX - rect.left;
        const distanceToRightEdge = rect.right - currentClientX;

        // Clear any existing scroll intervals
        clearInterval(autoScrollTimer);

        const performScrollAction = (amount) => {
          schedulerContainerRef.current.scrollLeft += amount;

          // 🚀 THE CRITICAL CORE FIX: Force FullCalendar to re-calculate its drop collision map
          // We synthesize a pointermove event that tricks FullCalendar into updating its cache
          const fakeMoveEvent = new PointerEvent('pointermove', {
            bubbles: true,
            cancelable: true,
            clientX: currentClientX -startDragX,
            clientY: currentClientY-startDragY,
            pointerType: originalEvent.pointerType || 'touch',
            isPrimary: true
          });

          // Dispatch it directly to the element FullCalendar's InferredElementDragging is listening to
          schedulerContainerRef.current.dispatchEvent(fakeMoveEvent);
        };

        if (distanceToRightEdge < rightEdgeThreshold && distanceToRightEdge > 0) {
          // Dragging near the RIGHT edge -> Scroll right
          autoScrollTimer = setInterval(() => {
            performScrollAction(scrollSpeed);
          }, 16);
        } else if (distanceToLeftEdge < leftEdgeThreshold && distanceToLeftEdge > 0) {
          // Dragging near the LEFT edge -> Scroll left
          autoScrollTimer = setInterval(() => {
            performScrollAction(-scrollSpeed);
          }, 16);
        }
      };

      const startDragGesture = (e, clientX, clientY, isTouch = false) => {

        startDragX = clientX;
        startDragY = clientY;

        const row = e.target.closest('.fc-draggable-node');
        if (!row) return;

        // Build a fresh tracking visual card at the root document body layer
        dragMirrorEl = document.createElement('div');
        dragMirrorEl.className = 'task-drag-mirror'; // Matches mirrorSelector exactly
        dragMirrorEl.innerText = `📋 ${row.dataset.title || 'Task'}`;
        
        // Sync dataset fields so FullCalendar can grab data attributes if needed
        dragMirrorEl.dataset.id = row.dataset.id;
        dragMirrorEl.dataset.title = row.dataset.title;

        Object.assign(dragMirrorEl.style, {
          position: 'fixed',
          pointerEvents: 'none', // Essential: stops the ghost from blocking cell drops
          zIndex: '999999',
          padding: '8px 12px',
          background: '#458cd3dc',
          color: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          opacity: '0.9',
          fontFamily: 'sans-serif',
          fontSize: '14px',
          left: `${clientX - 10}px`,
          top: `${clientY - 32}px`,
          display: 'none'
        });

        document.body.appendChild(dragMirrorEl);

        const moveDragGesture = (moveX, moveY) => {
          // TODO: Add guards here to ensure move is significant before showing mirror.
          if (dragMirrorEl) {
            dragMirrorEl.style.left = `${moveX - 10}px`;
            dragMirrorEl.style.top = `${moveY - 32}px`;
            dragMirrorEl.style.display = 'block';
          }
        };

        const handleMouseMove = (me) => {
          moveDragGesture(me.clientX, me.clientY);
          handleEdgeAutoScroll(me.clientX, me.clientX, me );
        }
        const handleTouchMove = (te) => {
          // 🛑 Critical: Stops mobile browsers from accidentally scrolling the screen text area!
          if (te.cancelable) te.preventDefault();
          const touch = te.touches[0];
          moveDragGesture(touch.clientX, touch.clientY);
          handleEdgeAutoScroll(touch.clientX, touch.clientX, touch ); // Feeds touch coordinate into auto-scroller
        };

        // Clean up tracking layers on drag release
        const endDragGesture = () => {
          window.removeEventListener('mousemove', handleMouseMove);
          window.removeEventListener('mouseup', endDragGesture);
          window.removeEventListener('touchmove', handleTouchMove, { passive: false });
          window.removeEventListener('touchend', endDragGesture);
          
          clearInterval(autoScrollTimer); // Stop scrolling immediately on drop

          if (dragMirrorEl) {
            dragMirrorEl.remove();
            dragMirrorEl = null;
          }
        };

        if (isTouch) {
          if (navigator.vibrate) navigator.vibrate(15); 
          window.addEventListener('touchmove', handleTouchMove, { passive: false });
          window.addEventListener('touchend', endDragGesture);
        } else {
          window.addEventListener('mousemove', handleMouseMove);
          window.addEventListener('mouseup', endDragGesture);
        }
      };

      // DESKTOP TRIGGER
      const handleMouseDown = (e) => {
        // if (isPlanningMode) {
          startDragGesture(e, e.clientX, e.clientY, false);
        // }
      };

      // MOBILE TOUCH TRIGGERS
      const handleTouchStart = (e) => {
        // if (!isPlanningMode) return;
        
        // Clear any leftover timer before starting a new one
        if (longPressTimer) clearTimeout(longPressTimer);
        
        const touch = e.touches[0];
        
        longPressTimer = setTimeout(() => {
          startDragGesture(e, touch.clientX, touch.clientY, true);
        }, 200); 
      };

      const handleTouchEndEarly = () => {
        if (longPressTimer) {
          clearTimeout(longPressTimer);
          longPressTimer = null;
        }
      };

      containerEl.addEventListener('mousedown', handleMouseDown);
      // Changed passive to false so touchstart context doesn't clash with preventing default layout behaviors downstream
      containerEl.addEventListener('touchstart', handleTouchStart, { passive: false });
      containerEl.addEventListener('touchend', handleTouchEndEarly);
      containerEl.addEventListener('touchmove', handleTouchEndEarly);

      return () => {
        fullCalendarDraggable.destroy();
        containerEl.removeEventListener('mousedown', handleMouseDown);
        containerEl.removeEventListener('touchstart', handleTouchStart);
        containerEl.removeEventListener('touchend', handleTouchEndEarly);
        containerEl.removeEventListener('touchmove', handleTouchEndEarly);
        
        if (longPressTimer) clearTimeout(longPressTimer);
        clearInterval(autoScrollTimer);
        if (dragMirrorEl) dragMirrorEl.remove();
      }
    }
  }, [activeId,
      items,
      selectedTaskTier,
      selectedPrimaryLifeArea,
      selectedTaskStatus,
      selectedEarliestCreationDateTime,
      selectedEarliestEstimatedCommencementDateTime,
      selectedLatestEstimatedCommencementDateTime,
      selectedEarliestPlannedDateTime,
      selectedLatestPlannedDateTime,
      selectedEarliestScheduledDateTime,
      selectedLatestScheduledDateTime,
      tabValue,
      activeCalendarViewModeIndex,
      activePlanningCalendarViewModeIndex,
      activeCalendarViewDisplayedDateRangeStartDateTime,
      isPlanningMode]);
      
  // const goalFlattenedItems = useMemo(() => {
  //   const flattenedTree = flattenTree(items).filter(
  //     (a) => {        
  //       return a.goal;
  //     });
  //   const collapsedItems = flattenedTree.reduce<string[]>(
  //     (acc, {children, collapsed, id}) =>
  //       collapsed && children.length ? [...acc, id] : acc,
  //     []
  //   );

  // return removeChildrenOf(
  //   flattenedTree,
  //   activeId ? [activeId, ...collapsedItems] : collapsedItems
  // );
  
  // }, [activeId, items]);

  
  const projected =
    activeId && overId
      ? getProjection(
          flattenedItems,
          activeId,
          overId,
          offsetLeft,
          indentationWidth
        )
      : null;

  const sensorContext: SensorContext = useRef({
    items: flattenedItems,
    offset: offsetLeft,
  });

  const [coordinateGetter] = useState(() =>
    sortableTreeKeyboardCoordinates(sensorContext, indicator, indentationWidth)
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter,
    })
  );

  const sortedIds = useMemo(() => flattenedItems.map(({id}) => id), [
    flattenedItems,
  ]);

  const activeItem = activeId
    ? flattenedItems.find(({id}) => id === activeId)
    : null;

  useEffect(() => {
    sensorContext.current = {
      items: flattenedItems,
      offset: offsetLeft,
    };
  }, [flattenedItems, offsetLeft]);

  const announcements: Announcements = {
    onDragStart({active}) {
      return `Picked up ${active.id}.`;
    },
    onDragMove({active, over}) {
      return getMovementAnnouncement('onDragMove', active.id, over?.id);
    },
    onDragOver({active, over}) {
      return getMovementAnnouncement('onDragOver', active.id, over?.id);
    },
    onDragEnd({active, over}) {
      return getMovementAnnouncement('onDragEnd', active.id, over?.id);
    },
    onDragCancel({active}) {
      return `Moving was cancelled. ${active.id} was dropped in its original position.`;
    },
  };

  function handleStatusChange(state) {
    setSelectedTaskStatus(state);
    // onStatusChanged(state.name);
  }

  function handlePrimaryLabelChange(primaryLabel) {
    setSelectedPrimaryLifeArea(primaryLabel);
    setPageTitle(headerText+' - '+primaryLabel.name);

    if(primaryLabel) {
      // onPrimaryLabelChanged(primaryLabel.name);
    }
  }

  function handleSelectedEarliestCreationDateTimeChange(newDateTime) {
    setSelectedEarliestCreationDate(newDateTime);
  }

  function handleSelectedEarliestEstimatedCommencementDateTimeChange(newDateTime) {
    setSelectedEarliestEstimatedCommencementDateTime(newDateTime);
  }

  function handleSelectedLatestEstimatedCommencementDateTimeChange(newDateTime) {
    setSelectedLatestEstimatedCommencementDateTime(newDateTime);
  }

  function handleSelectedEarliestPlannedDateTimeChange(newDateTime) {
    setSelectedEarliestPlannedDateTime(newDateTime);
  }

  function handleSelectedLatestPlannedDateTimeChange(newDateTime) {
    setSelectedLatestPlannedDateTime(newDateTime);
  }

  function handleSelectedEarliestScheduledDateTimeChange(newDateTime) {
    setSelectedEarliestScheduledDateTime(newDateTime);
  }

  function handleSelectedLatestScheduledDateTimeChange(newDateTime) {
    setSelectedLatestScheduledDateTime(newDateTime);
  }

  //TODO: Remove this.
  function useFocus() {
    const inputRef = useRef(null);
    return inputRef;
  }

  function useFocuses() {
    const inputRef = useRef([]); //TODO: Use a Map instead of an array.
  
    useEffect(() => {
      console.log('Is focus effect firing? inputRef.current = ' + inputRef.current);
      if(focusToTreeItemIdRef && focusToTreeItemIdRef.current) {
        // if(focusToTreeItemId) {
        console.log('Setting focus in effect to: ' + focusToTreeItemIdRef.current);
        const item = filteredFlattenedItems.find( ({id})=>id === focusToTreeItemIdRef.current );
        const itemIndex = item ? filteredFlattenedItems.indexOf(item) : null;
        console.log('Index in effect: ' + itemIndex);
        const ref = (itemIndex > -1) ? inputRefs.current[itemIndex]:null;
        ref?.focus();
      };
      // return () => {setFocusToTreeItemId(null)};
    }, [focusToTreeItemId, items]);
  
    return inputRef;
  }

  function CustomTabPanel(props) {
    const { children, tabValue, index, className, ...other } = props;
  
    return (
      <Box
        role="tabpanel"
        sx={{ 
              // Swapping to none completely hides the container and releases its space
              display: tabValue === index ? 'flex' : 'none', 
              flexDirection: 'column',
              minWidth: 0,
              maxWidth: '100% !important', 
              width: '100%',
              gap: 2,
              flexGrow: 1,
              '& .fc-view-harness': {
                flexGrow: 1,
                minHeight: 0,
                minWidth: 0,
                maxWidth: '100% !important', 
                width: '100%',
            }, 
            }}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        className={className}
        {...other}
      >
        {tabValue === index && <Box 
            sx={{ p: 0,
                  position: 'relative',
                  display: 'flex !important', 
                  flexDirection: 'column !important',
                  minWidth: 0,
                  minHeight: 0,
                  maxWidth: '100% !important', 
                  maxHeight: '100% !important', 
                  width: '100%',   
                  height: '100%',     
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  '& .fc-view-harness': {
                    flexGrow: 1,
                    minHeight: 0
                  },
            }}
          >{children}
        </Box>}
      </Box>
    );
  }

  CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    tabValue: PropTypes.number.isRequired,
    className: PropTypes.string
  };

  // const DnDCalendar = withDragAndDrop(Calendar)
  // Setup the localizer by providing the moment (or globalize, or Luxon) Object
  // to the correct localizer.
  // const localizer = momentLocalizer(moment) // or globalizeLocalizer

  function handleOnFocus(id: UniqueIdentifier | null): void {
    
    setFocusToTreeItemId(focusToTreeItemIdRef ? focusToTreeItemIdRef.current = id:null);
    console.log('Focus ID : ' + id);
    console.log('Active ID (useState) : ' + focusToTreeItemId);
  }

  function handleShowDataConfigPanelButtonClicked(): void {
    setDataConfigPanelHidden(false);
  }
  
  function handleCloseDataConfigPanelButtonClicked(): void {
    setDataConfigPanelHidden(true);
  }

  const theme = useTheme();
  
  // Check the screen size ONCE for the entire application
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const reactWindowListRef = useListRef(null);

  const schedulerContainerRef = useRef(null);

  const setListRef = (instance) => {
  // Only update the state if it's a completely brand new instance object.

    if (instance) {
      reactWindowListRef.current = instance;
    }
  };

  const topVisibleIndex = useRef(0);
  const treeVisibleTopScroll = useRef(0);
  const treeVisibleLeftScroll = useRef(0);

  const taskListScrollTargetRef = useRef(0);
  const schedulerHorizontalScrollPostionRef = useRef(0);

  useLayoutEffect(() => {

    if(schedulerContainerRef?.current) {
      schedulerContainerRef.current.scrollLeft = schedulerHorizontalScrollPostionRef;
    }
    
    if ( reactWindowListRef?.current && (taskListScrollTargetRef.current || treeVisibleLeftScroll.current)) {

      const targetIndex = taskListScrollTargetRef.current;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (reactWindowListRef.current) {
            // console.log("Are we attempting to call scrollToRow? TargetIndex: "+targetIndex)

            // Don't use topScroll and scrollToRow but the later might still be useful...
            
            // reactWindowListRef.current.scrollToRow({
            //   index: targetIndex,
            //   align: 'start',
            //   behavior: 'instant'
            // });

            // ...but always set the horizontal scroll too... 
            queueMicrotask(() => {
              if (reactWindowListRef.current && reactWindowListRef.current.element) {
                
                // Don't fight react-window if we use scrollToRow...
                reactWindowListRef.current.element.scrollTop = treeVisibleTopScroll?.current;
                reactWindowListRef.current.element.scrollLeft = treeVisibleLeftScroll?.current;
              }
            });

          }
        });
      });

    }
  }); 

  const mutateTree = (listOpHandler) => {
    
    taskListScrollTargetRef.current = topVisibleIndex.current;

    listOpHandler();

  };

  // useCallback ensures the function reference remains identical across renders.
  // This prevents react-window from tearing down its scroll listeners on state updates.
  const handleRowsRendered = useCallback((visibleRows, allRows) => {
    if (visibleRows && typeof visibleRows.startIndex === 'number') {
      topVisibleIndex.current = visibleRows.startIndex;
    }
  }, []); // Empty array ensures the function reference remains stable

  const getRowKey = useCallback((index) => {
    return filteredFlattenedItems[index].id;
  }, [filteredFlattenedItems]); // Updates safely when the data array changes identity

  const handleBlur = (event) => {
    taskListScrollTargetRef.current = topVisibleIndex.current;
  };

  const recordFinalPosition = useCallback(
    debounce((scrollTop: number) => {
      console.log("Locked final position via debounce:", scrollTop);
      taskListScrollTargetRef.current = topVisibleIndex.current;
      treeVisibleTopScroll.current = reactWindowListRef.current?.element?.scrollTop;
      treeVisibleLeftScroll.current = reactWindowListRef.current?.element?.scrollLeft;
    }, 150),
    []
  );

  const handleSchedulerScroll = (scrollEvent) => {
      // if(schedulerHorizontalScrollPostionRef?.current) {
        schedulerHorizontalScrollPostionRef.current = scrollEvent.target.scrollLeft;
      // };
    };

  const handleTaskListScroll = useCallback(({ scrollOffset }: { scrollOffset: number }) => {
    recordFinalPosition(scrollOffset);
 
  }, [recordFinalPosition]);

  
  const RenderRow = useMemo(() => {
    return ({ index, style, isDraggable, indendationWidthOverride }: RowComponentProps<{}>) => {
      const task = filteredFlattenedItems[index];
      
      // If needed for a future update:
      // const { attributes, listeners, setNodeRef } = useSortable({ id: task.id });

      return (
      <div style={{
             ...style,
             willChange: 'transform', //isMobile ? 'transform' : 'auto',
             whiteSpace: 'nowrap',
             textOverflow: 'ellipsis',
             boxSizing: 'border-box',
             userSelect: 'none',
             WebkitUserSelect: 'none', 
             MozUserSelect: 'none',    
             msUserSelect: 'none',
           }}
           className="fc-draggable-node"
           key={'RenderRow-'+task.id}
           data-id={task.id}
           data-title={task.title}
           data-event={JSON.stringify({ title: task.title, create: true })} 
           >
        <div key={'RenderRowInner-'+task.id}>
          {((index != 0) && (task.parentId === null)) && <div key={`spacer1-${task.id}`} style={{ height: '20px' }} />}
          {index != 0 && task.parentId !== null && 
          (filteredFlattenedItems.find(({id})=> id === task.parentId) === undefined) &&
          (filteredFlattenedItems[index-1]['parentId'] !== task.parentId) && 
          <div key={`spacer2-${task.id}`} style={{ height: '20px' }} />}
          <SortableTreeItem 
            key={'SortableTreeItem-'+task.id}
            id={task.id}            
            value={task.title ? task.title: task.id.toString()}
            data-event={'{ "title": "'+ task.title +'", "id": "' + task.id.toString() + '"}'}
            draggable={isDraggable}
            inputRef={task.id === focusToTreeItemId ? titleInputRef : undefined}
            inputRefs={inputRefs}
            index={index}
            estimatedCommencementDate={task.estimatedCommencementDate}
            idealCompletionDate={task.idealCompletionDate}
            latestPossibleCompletionDate={task.latestPossibleCompletionDate}
            done={task.done}
            depth={task.id === activeId && projected ? projected.depth : task.depth}
            goal={task.goal}
            indentationWidth={indendationWidthOverride !== undefined? indendationWidthOverride : indentationWidth}
            indicator={indicator}
            primaryLabel={task.primaryLabel}
            status={task.status}
            // selected={id === selectedItem}
            collapsed={Boolean(task.collapsed && task.children.length)}
            onCollapse={
              collapsible && task.children.length
                ? () => handleCollapse(task.id)
                : undefined
            }
            onDoneChanged={() => handleDone(task.id)}
            onExpansionChanged={(isExpanded) => handleTaskExpansionChanged(task.id, isExpanded)}
            onGoalChanged={() => handleGoal(task.id)}
            insertNewItemAfter={(ctrlCreate) => insertNewItemAfter(task.id, ctrlCreate)}
            isMobile={isMobile}
            isInitiallyExpanded={expandedIdsMap[task.id]}
            onEstimatedCommencementDateChanged={(newDate) => handleOnEstimatedCommencementDateChanged(task.id, newDate)}
            onIdealDateChanged={(newDate) => handleOnIdealDateChanged(task.id, newDate)}
            onLatestDateChanged={(newDate) => handleOnLatestDateChanged(task.id, newDate)}
            onPrimaryLabelChanged={(newPrimaryLabel) => handleOnPrimaryLabelChanged(task.id, newPrimaryLabel)}
            onStatusChanged={(newStatus) => handleOnStatusChanged(task.id, newStatus)}
            onTitleChanged={(newTitle, focussedValue) => handleOnTitleChanged(task.id, newTitle, focussedValue)}
            handleUpArrowPressed={() => handleOnUpArrowPressed(task.id)}
            handleDownArrowPressed={() => handleOnDownArrowPressed(task.id)}
            onDuplicate={() => handleDuplicateItem(task.id)}
            onRemove={removable ? () => handleRemove(task.id) : undefined}
            onSelected={() => handleItemSelected(task.id)}
            onNavTo={() => handleNavToItem(task.id)}
          />
        </div>
      </div>
    )};
  }, [filteredFlattenedItems, isMobile, expandedIdsMap]);

  return (
    <div className="contentPanels">
      {home && 
      <>
        <label className="App-Title">{headerText}</label>
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column',  hieght: '100%', width: '100%', minHeight: 0, overflow: 'hidden',
            position: 'relative',
            '& .fc': {
              height: '100% !important',
              display: 'flex !important',
              flexDirection: 'column !important'
            },
            '& .fc-view-harness': {
              flexGrow: 1,
              minHeight: 0
            },
            '& .fc-header-toolbar': {
              padding: '12px 16px 4px 16px', // Safely spaces your calendar header buttons
              margin: '0 !important'
            },
            '& .fc-footer-toolbar': {
              padding: '12px 4px 16px 16px', // Safely spaces your calendar header buttons
              margin: '0 !important'
            },
            '& .fc-view': {
              // padding: '0 16px 16px 16px'  // Safely pads the main calendar grid canvas
            }
         }}>
          <div className='App-Home' style={{ height: isMobile ? '90%': '100%', }}>
          <div className="App-HomeAgenda">
            <FullCalendar
              // ref={calendarRef}
              plugins={[ dayGridPlugin, multiMonthPlugin, timeGridPlugin, listPlugin, interactionPlugin ]}
              editable={true}
              eventResizableFromStart={true}
              eventColor={isPlanningMode? "#84baecff" : ""}
              initialView="agendaDay"//{calendarViewModes[7].name}
              timeZone='local'
              initialDate={activeCalendarViewDisplayedDateRangeStartDateTime}
              height="100%"
              headerToolbar={{
                              left: "today prev",
                              center: "title",
                              right: "next"
                            }}
              footerToolbar={{
                // left: "today",
                // center: "title",
                right: "agendaWeek agendaDay"
              }}
              views={{
                      agendaWeek: {
                        type: 'list',
                        duration: { days: 7 },
                        buttonText: 'Week'
                      },
                      agendaDay: {
                        type: 'list',
                        duration: { days: 1 },
                        buttonText: 'Day'
                      }
                    }}
              nowIndicator={true}
              scrollTime={'8:00:00'}
              weekends={true}
              multiMonthMaxColumns={2}
              // defaultAllDay={isPlanningMode ? true : false}
              events={calendarFilteredFlattenedItems}
              eventChange={handleEventChangedInCalendar}
              // viewClassNames={handleCalendarViewChange}
              datesSet={handleCalendarViewDatesSet}
              aspectRatio={1}
              handleWindowResize={1}
              buttonText={{
                  today:    'Today',
                  // year:     'Year',
                  // month:    'Month',
                  // week:     'Week',
                  // day:      'Day',
                  // list:     'Agenda'
                }}
              // nowIndicatorClassNames="fc-now-indicator"
            />
          </div>
          <div className="App-HomeCalendar">
            <FullCalendar
              // className="App-SchedulerCalendar"
              // ref={calendarRef}
              plugins={[ dayGridPlugin, multiMonthPlugin, timeGridPlugin, listPlugin, interactionPlugin ]}
              editable={true}
              eventResizableFromStart={true}
              droppable={true}
              eventColor={isPlanningMode? "#84baecff" : ""}
              initialView={calendarViewModes[8].name}
              timeZone='local'
              initialDate={activeCalendarViewDisplayedDateRangeStartDateTime}
              height={isMobile ? "80%": "100%"}
              headerToolbar={{
                              left: "today prev",
                              center: "title",
                              right: "next"
                            }}
              footerToolbar={{
                // left: "today prev",
                // center: "title",
                right: "dayGridYear,timeGridWeek,timeGridThreeDay,timeGridDay"
              }}                            
              views={{
                      timeGridThreeDay: {
                        type: 'timeGrid',
                        duration: { days: 3 },
                        buttonText: '3 Days'
                      }
                    }}
              // height={665}
              // dayCount={1}
              nowIndicator={true}
              scrollTime={'8:00:00'}
              weekends={true}
              multiMonthMaxColumns={1}
              // defaultAllDay={isPlanningMode ? true : false}
              events={calendarFilteredFlattenedItems}
              eventChange={handleEventChangedInCalendar}
              // viewClassNames={handleCalendarViewChange}
              datesSet={handleCalendarViewDatesSet}
              aspectRatio={1}
              buttonText={{
                  today:    'Today',
                  year:     'Year',
                  month:    'Month',
                  week:     'Week',
                  day:      'Day',
                  list:     'Agenda'
                }}
                handleWindowResize={3}
              // nowIndicatorClassNames="fc-now-indicator"
            />
          </div>
          {isMobile && <Box sx={{minHeight:'100px', height: '100px', }}/>}
          </div>
        </Box>
      </>}
      {!home && 
      <>
        {isDataConfigPanelHidden &&
        <>
          <div className="App-TitleHeader-Collapsed">
            <div className="App-TitleHeader-LeftElements">
              <Autocomplete
                className="App-Title"
                value={pageTitle}
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string') {
                    setPageTitle({
                      title: headerText+' - '+newValue,
                    });
                    setSelectedPrimaryLifeArea(primaryLifeAreas.find((item) => item.name === newValue));
                  } else if (newValue && newValue.inputValue) {
                    // Create a new value from the user input
                    setPageTitle({
                      title: newValue.inputValue,
                    });
                  } else {
                    setPageTitle(headerText);
                    setSelectedPrimaryLifeArea(primaryLifeAreas[10]);
                  }
                }}
                filterOptions={(options, params) => {
                  const filtered = titleFilter(options, params);

                  const { inputValue } = params;
                  // Suggest the creation of a new value
                  const isExisting = options.some((option) => inputValue === option.title);
                  if (inputValue !== '' && !isExisting) {
                    filtered.push({
                      inputValue,
                      title: `Add "${inputValue}"`,
                    });
                  }

                  return filtered;
                }}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                id="free-solo-with-text-demo"
                options={primaryLifeAreas.map(({ name }) => name)}
                popupIcon={<ArrowDropDown />} 
                forcePopupIcon={true} 
                getOptionLabel={(option) => {
                  // Value selected with enter, right from the input
                  if (typeof option === 'string') {
                    return option;
                  }
                  // Add "xxx" option created dynamically
                  if (option.inputValue) {
                    return option.inputValue;
                  }
                  // Regular option
                  return option.title;
                }}
                // renderOption={(props, option) => {
                //   const { key, ...optionProps } = props;
                //   return (
                //     <li className="App-Title" key={key} {...optionProps}>
                //       {option.title}
                //     </li>
                //   );
                // }}
                sx={{ display: 'flex',
                      flexDirection: 'row',
                      minWidth: isMobile? '33%' : '200px',
                      width: isMobile? '66%' : '66%',
                      maxWidth: isMobile? '66%' : '100%',
                      height: 'min-content', 
                      borderWidth: '0px',
                      '& .MuiOutlinedInput-root': {
                        paddingTop: '0px !important',
                        paddingBottom: '0px !important',
                        borderWidth: '0px',
                        borderRadius: '12px',

                        color: '#000000',          // Input text color
                        backgroundColor: '#e0e0e005', // Input background color
                        fontSize: isMobile? '27px' : '41px',
                        fontFamily: "'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif"
                      },
                      '& .MuiOutlinedInput-input': {
                        whiteSpace: 'normal !important',   // Allows text to wrap naturally
                        wordBreak: 'break-word',          // Prevents long words from blowing out the edge
                        overflow: 'visible !important',   // Un-hides the overflow text
                        paddingTop: '0px !important',
                        paddingBottom: '0px !important',
                      },
                      '& .MuiAutocomplete-option': {

                        color: '#000000 !important', // Forces list item text color
                        backgroundColor: '#ffffff05', // Input background color
                        fontSize: isMobile? '27px' : '41px',
                        fontFamily: "'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif"
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none !important',
                      },
                  }}
                freeSolo
                resetHighlightOnMouseLeave
                renderInput={(params) => (
                      <TextField 
                      {...params} 
                      
                      // 2. THE SECRET SAUCE: Forces a textarea layout that allows text wrapping
                      multiline 
                      maxRows={3} // Stops it from growing past two lines
                      
                      sx={{
                        // 3. Clean up the textarea formatting so it handles alignment cleanly
                        '& .MuiInputBase-input': {
                          whiteSpace: 'normal !important',
                          wordBreak: 'break-word',
                          lineHeight: '1.2', // Tightens line spacing to fit your custom height
                          display: 'flex',
                          alignItems: 'center'
                        }
                      }}
                    />
                )}
                slotProps={{
                  paper: {
                    sx: {
                      backgroundColor: '#fafafaff', // 1. Dropdown background color
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      
                      // 2. Dropdown text color for all options
                      '& .MuiAutocomplete-option': {
                        color: '#000000ff', 
                      },
                      
                      // 3. Optional: Color when you hover over an item
                      '& .MuiAutocomplete-option[data-focus="true"]': {
                        backgroundColor: '#333333', 
                        color: '#ffffff',
                      },
                      
                      // 4. Optional: Color when an item is selected
                      '& .MuiAutocomplete-option[aria-selected="true"]': {
                        backgroundColor: '#ecececff',
                        color: '#ffffff',
                      }
                    },
                  },
                }}
              />
              {/* <label className="App-Title">{headerText}</label> */}
              <Tooltip title='Filter Settings'>
                <div className="App-TitleButtons" onClick={handleShowDataConfigPanelButtonClicked}>
                    <Tune />
                </div>
              </Tooltip>
            </div>
            <Box
              component="div"
              sx={{
                display: { xs: 'none', md: 'block' },
              }}
            >
              <div className="App-TabControlPanel">
                <IconLabelTabs tabValue={tabValue} setTabValue={setTabValue}/>
              </div>
            </Box>
            <Tooltip title='Undo (ctrl + z)'>
              <div className="App-TitleButtons-Right" onClick={handleUndo}>
                  <UndoOutlined/>
              </div>
            </Tooltip>
          </div>
        </>}
        {!isDataConfigPanelHidden &&
        <>
          <div className="App-TitleHeader">
            <Autocomplete
              className="App-Title"
              value={pageTitle}
              onChange={(event, newValue) => {
                if (typeof newValue === 'string') {
                  setPageTitle({
                    title: headerText+' - '+newValue,
                  });
                  setSelectedPrimaryLifeArea(primaryLifeAreas.find((item) => item.name === newValue));
                } else if (newValue && newValue.inputValue) {
                  // Create a new value from the user input
                  setPageTitle({
                    title: newValue.inputValue,
                  });
                } else {
                  setPageTitle(headerText);
                  setSelectedPrimaryLifeArea(primaryLifeAreas[10]);
                }
              }}
              filterOptions={(options, params) => {
                const filtered = titleFilter(options, params);

                const { inputValue } = params;
                // Suggest the creation of a new value
                const isExisting = options.some((option) => inputValue === option.title);
                if (inputValue !== '' && !isExisting) {
                  filtered.push({
                    inputValue,
                    title: `Add "${inputValue}"`,
                  });
                }

                return filtered;
              }}
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              id="free-solo-with-text-demo"
              options={primaryLifeAreas.map(({ name }) => name)}
              popupIcon={<ArrowDropDown />} 
              forcePopupIcon={true} 
              getOptionLabel={(option) => {
                // Value selected with enter, right from the input
                if (typeof option === 'string') {
                  return option;
                }
                // Add "xxx" option created dynamically
                if (option.inputValue) {
                  return option.inputValue;
                }
                // Regular option
                return option.title;
              }}
              // renderOption={(props, option) => {
              //   const { key, ...optionProps } = props;
              //   return (
              //     <li className="App-Title" key={key} {...optionProps}>
              //       {option.title}
              //     </li>
              //   );
              // }}
              sx={{ display: 'flex',
                    flexDirection: 'row',
                    minWidth: isMobile? '33%' : '200px',
                    width: isMobile? '66%' : '50%',
                    maxWidth: isMobile? '66%' : '100%',
                    height: 'min-content', 
                    borderWidth: '0px',
                    '& .MuiOutlinedInput-root': {
                      paddingTop: '0px !important',
                      paddingBottom: '0px !important',
                      borderWidth: '0px',
                      borderRadius: '12px',

                      color: '#000000',          // Input text color
                      backgroundColor: '#e0e0e005', // Input background color
                      fontSize: isMobile? '27px' : '41px',
                      fontFamily: "'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif"
                    },
                    '& .MuiOutlinedInput-input': {
                      whiteSpace: 'normal !important',   // Allows text to wrap naturally
                      wordBreak: 'break-word',          // Prevents long words from blowing out the edge
                      overflow: 'visible !important',   // Un-hides the overflow text
                      paddingTop: '0px !important',
                      paddingBottom: '0px !important',
                    },
                    '& .MuiAutocomplete-option': {

                      color: '#000000 !important', // Forces list item text color
                      backgroundColor: '#ffffff05', // Input background color
                      fontSize: isMobile? '27px' : '41px',
                      fontFamily: "'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif"
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none !important',
                    },
                }}
              freeSolo
              resetHighlightOnMouseLeave
              renderInput={(params) => (
                    <TextField 
                    {...params} 
                    
                    // 2. THE SECRET SAUCE: Forces a textarea layout that allows text wrapping
                    multiline 
                    maxRows={3} // Stops it from growing past two lines
                    
                    sx={{
                      // 3. Clean up the textarea formatting so it handles alignment cleanly
                      '& .MuiInputBase-input': {
                        whiteSpace: 'normal !important',
                        wordBreak: 'break-word',
                        lineHeight: '1.2', // Tightens line spacing to fit your custom height
                        display: 'flex',
                        alignItems: 'center'
                      }
                    }}
                  />
              )}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: '#fafafaff', // 1. Dropdown background color
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    
                    // 2. Dropdown text color for all options
                    '& .MuiAutocomplete-option': {
                      color: '#000000ff', 
                    },
                    
                    // 3. Optional: Color when you hover over an item
                    '& .MuiAutocomplete-option[data-focus="true"]': {
                      backgroundColor: '#333333', 
                      color: '#ffffff',
                    },
                    
                    // 4. Optional: Color when an item is selected
                    '& .MuiAutocomplete-option[aria-selected="true"]': {
                      backgroundColor: '#ecececff',
                      color: '#ffffff',
                    }
                  },
                },
              }}
            />
            {/* <label className="App-Title">{headerText}</label> */}
            <Tooltip  title='Undo (ctrl + z)'>
              <div className="App-TitleButtons-Right" onClick={handleUndo}>
                  <UndoOutlined/>
              </div>
            </Tooltip>
          </div>
          <div className='App-DataPanel'>
            <div className="App-ViewConfigurationPanel">
              <div className="App-FilterSettingsPanel">
                <div className="App-FilterPanelHeader">
                  <label className="App-FilterPanelHeaderLabel">Filter for:</label>
                  <Close marginLeft='auto' onClick={handleCloseDataConfigPanelButtonClicked}/>
                </div>
                <div className="App-FilterControls">
                  <div className="App-FilterControl">
                    {/* <label className="App-FilterLabels" style={{width: '100%', maxWidth: '100%',}}>Status:</label> */}
                    <label className="App-FilterLabelHeadings" >Status:</label>
                    <Listbox value={selectedTaskStatus} onChange={handleStatusChange}>
                      <ListboxButton>
                        {selectedTaskStatus.name}
                      </ListboxButton>
                      <ListboxOptions anchor="bottom" 
                                      className="ListBoxItem">
                        {taskStatuses.map((state) => (
                          <ListboxOption key={state.id} value={state} className="ListBoxItem">
                            {state.name}
                          </ListboxOption>
                        ))}
                      </ListboxOptions>
                    </Listbox>
                  </div>
                  <div className="App-FilterControl">
                    <label className="App-FilterLabelHeadings">Life Area:</label>
                    <Listbox value={selectedPrimaryLifeArea} onChange={handlePrimaryLabelChange}>
                      <ListboxButton>{selectedPrimaryLifeArea.name}</ListboxButton>
                        <ListboxOptions anchor="bottom">
                          {primaryLifeAreas.map((primaryLabel) => (
                            <ListboxOption key={primaryLabel.id} value={primaryLabel} className="ListBoxItem">
                              {primaryLabel.name}
                            </ListboxOption>
                          ))}
                      </ListboxOptions>
                    </Listbox>
                  </div>
                  <div className="App-FilterControl">
                    <label className="App-FilterLabelHeadings">Task Tier:</label>
                    <Listbox value={selectedTaskTier} onChange={setSelectedTaskTier}>
                      <ListboxButton>{selectedTaskTier.name}</ListboxButton>
                        <ListboxOptions anchor="bottom">
                          {taskTiers.map((itemType) => (
                            <ListboxOption key={itemType.id} value={itemType} className="ListBoxItem">
                              {itemType.name}
                            </ListboxOption>
                          ))}
                      </ListboxOptions>
                    </Listbox>
                  </div>
                  <div className="App-FilterControl">
                    <label className="App-FilterLabelHeadings">Created After:</label>
                    <input name='selectedTaskCreatedOnAfterDateTime' type='datetime-local' 
                          value={selectedEarliestCreationDateTime} onChange={(e) => handleSelectedEarliestCreationDateTimeChange(e.target.value)}/>
                  </div>
                  <div className="App-FilterControl">
                    <label className="App-FilterLabelHeadings">Estimated Commencement:</label>
                  </div>
                  <div className="App-FilterControl">
                    <label className="App-FilterLabels">After:</label>
                    <input name='selectedEarliestEstimatedCommencementDateTime' type='datetime-local'
                          value={selectedEarliestEstimatedCommencementDateTime} onChange={(e) => handleSelectedEarliestEstimatedCommencementDateTimeChange(e.target.value)}/>
                    <label className="App-FilterLabels">Before:</label>
                    <input name='selectedLateestEstimatedCommencementDateTime' type='datetime-local' 
                          value={selectedLatestEstimatedCommencementDateTime} onChange={(e) => handleSelectedLatestEstimatedCommencementDateTimeChange(e.target.value)}/>
                  </div>
                  <div className="App-FilterControl">
                    <label className="App-FilterLabelHeadings">Planned:</label>
                  </div>
                  <div className="App-FilterControl">
                    <label className="App-FilterLabels">After:</label>
                    <input name='selectedEarliestPlannedDateTime' type='datetime-local'
                          value={selectedEarliestPlannedDateTime} onChange={(e) => handleSelectedEarliestPlannedDateTimeChange(e.target.value)}/>
                    <label className="App-FilterLabels">Before:</label>
                    <input name='selectedLatestPlannedDateTime' type='datetime-local' 
                          value={selectedLatestPlannedDateTime} onChange={(e) => handleSelectedLatestPlannedDateTimeChange(e.target.value)}/>
                  </div>
                  <div className="App-FilterControl">
                    <label className="App-FilterLabelHeadings">Scheduled:</label>
                  </div>
                  <div className="App-FilterControl">
                    <label className="App-FilterLabels">After:</label>
                    <input name='selectedEarliestScheduledDateTime' type='datetime-local'
                          value={selectedEarliestScheduledDateTime} onChange={(e) => handleSelectedEarliestScheduledDateTimeChange(e.target.value)}/>
                    <label className="App-FilterLabels">Before:</label>
                    <input name='selectedLatestScheduledDateTime' type='datetime-local' 
                          value={selectedLatestScheduledDateTime} onChange={(e) => handleSelectedLatestScheduledDateTimeChange(e.target.value)}/>
                  </div>
                  {/* <div className="App-FilterControl">
                    <MyButton className="App-FilterControl"
                              items={items} 
                              setItems={setItems}
                              specifiedCreatedTaskOwnerId={getAuth().currentUser?.uid}
                              goal={selectedTaskTier === taskTiers[2] ? true: false}
                              primaryLabel={(selectedPrimaryLifeArea && selectedPrimaryLifeArea.name === primaryLifeAreas[10].name)? primaryLifeAreas[0].name : selectedPrimaryLifeArea? selectedPrimaryLifeArea.name: primaryLifeAreas[0].name} />
                  </div> */}
                </div>
              </div>
            </div>
            <Box
              component="div"
              sx={{
                display: { xs: 'none', md: 'block' },
              }}
              >
              <div className="App-TabControlPanel">
                <IconLabelTabs tabValue={tabValue} setTabValue={setTabValue}/>
              </div>
            </Box>
   
          </div>
        </>}
          
        <DndContext
          accessibility={{announcements}}
          sensors={sensors}
          collisionDetection={closestCenter}
          measuring={measuring}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
        <>
          <Box 
            component="form"
            sx={{ visibility: selectedNavigationTaskId ? 'visable' : 'hidden',
                  borderTopLeftRadius: '12px',
                  borderTopRightRadius: '12px',
                  display: 'flex',
                  backgroundColor: 'lightgrey', //'rgba(233, 233, 235, 1)',
                  '& .MuiTextField-root': { m: 1, width: '25ch' },
                  marginTop: '3px'
                 }}
            noValidate
            autoComplete="off">
              <ArrowUpward onClick={handleShowParentBranch}
                           sx={{ visibility: selectedNavigationTaskParentFlattenedItem ? 'visable' : 'hidden',
                                 borderRadius: '15px',
                                 color: 'lightgray', 
                                 backgroundColor: 'gray',
                                 margin: '3px',
                                 opacity: '1',
                                 }}>
              </ArrowUpward>
            {selectedNavigationTaskId && findItemDeep(items, selectedNavigationTaskId) && Array(findItemDeep(items, selectedNavigationTaskId) as TreeItem).map(({id, title, children, collapsed, estimatedCommencementDate, idealCompletionDate, latestPossibleCompletionDate, parentId, primaryLabel, status, done, depth, goal}, index) => (
            <>
              <Breadcrumbs aria-label="breadcrumb">
                {/* <Link underline="hover" color="inherit" href="/">
                  MUI
                </Link>
                <Link
                  underline="hover"
                  color="inherit"
                  href="/material-ui/getting-started/installation/"
                >
                  Core
                </Link> */}
                <Typography sx={{ //backgroundColor: 'gray',
                                  color: 'black',
                                  borderRadius: '15px',
                                  margin: '3px',
                                  opacity: '1',
                 }}>{title}</Typography>
              </Breadcrumbs>
              <Clear sx={{ borderRadius: '15px',
                           color: 'lightgray',
                           backgroundColor: 'gray',
                           margin: '3px',
                           opacity: '1', }} 
                     onClick={handleShowAllFilteredTasks}>
              </Clear>
            </>
            ))}
          </Box>
        </>
        <CustomTabPanel className="App-TabPanel" tabValue={tabValue} index={0} >
            {/* <button onClick={handleTestAdd}>Prepend Node</button> */}
        <div className="App-Tree" style={{paddingLeft: isMobile ? '10px':'32px'}}> 
          {/* onBlur={handleBlur} onMouseLeave={handleBlur}> */}
        <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
          <div style={{ width: '100%', height: '100%' }}>
            <List
              onScroll={handleTaskListScroll}
              key={`list-length-${filteredFlattenedItems.length}`}
              listRef={setListRef}
              rowCount={filteredFlattenedItems.length}
              rowHeight={(index) => getRowHeight(index)}
              onRowsRendered={handleRowsRendered}
              overscanCount={2}   // Pre-renders n rows off-screen for smooth scrolling
              rowComponent={RenderRow}
              rowProps={{
                // isDraggable: false,
                // indendationWidthOverride: 0
              }}//{{}}
              rowKey={(index) => getRowKey(index)}
              style={{
                // width: '100%',
                height: '100%', // Dictated completely by the style prop!
                // contain: 'layout paint style'
                }}
              // defaultWidth={800}  
              defaultHeight={1024} 
              // useIsScrolling={true}
              // initialScrollOffset={currentScrollOffset.current}
            />
          </div>

          {createPortal(
            <DragOverlay
              dropAnimation={dropAnimationConfig}
              modifiers={indicator ? [adjustTranslate] : undefined}
            >            
              {activeId && activeItem ? (
                <SortableTreeItem
                  id={activeId}
                  depth={activeItem.depth}
                  clone
                  childCount={getChildCount(items, activeId) + 1}
                  value={activeItem.title}
                  indentationWidth={indentationWidth}
                />
              ) : null}
            </DragOverlay>,
            document.body
          )}
        </SortableContext>
      
      </div>
      </CustomTabPanel>
      <CustomTabPanel className="App-TabPanel" tabValue={tabValue} index={1}>
        <div className="App-Graph">        

          <RelatedNetwork data={filteredFlattenedItems} ></RelatedNetwork>

        </div>
      </CustomTabPanel>
      <CustomTabPanel tabValue={tabValue} index={2}>
        <div className="App-Tree">
            {dateOrderedFlattenedItems.map(({id,
                                            title,
                                            children,
                                            collapsed,
                                            idealCompletionDate,
                                            latestPossibleCompletionDate,
                                            done,
                                            depth,
                                            goal}) => (
              <SortableTreeItem            
                key={id}
                id={id}
                value={title ? title: id.toString()}
                draggable={false}
                idealCompletionDate={idealCompletionDate}
                inputRefs={inputRefs}
                latestPossibleCompletionDate={latestPossibleCompletionDate}
                done={done}
                depth={id === activeId && projected ? projected.depth : depth}
                goal={goal}
                indentationWidth={0}
                indicator={indicator}
                // collapsed={Boolean(collapsed && children.length)}
                collapsed={false}
                onCollapse={false}
                onDoneChanged={() => handleDone(id)}
                onGoalChanged={() => handleGoal(id)}
                insertNewItemAfter={undefined}
                onIdealDateChanged={(newDate) => handleOnIdealDateChanged(id, newDate)}
                onLatestDateChanged={(newDate) => handleOnLatestDateChanged(id, newDate)}
                onPrimaryLabelChanged={(newPrimaryLabel) => handleOnPrimaryLabelChanged(id, newPrimaryLabel)}
                onStatusChanged={(newStatus) => handleOnStatusChanged(id, newStatus)}
                onTitleChanged={(newTitle) => handleOnTitleChanged(id, newTitle)}
                onRemove={removable ? () => handleRemove(id) : undefined}
                onNavTo={() => handleNavToItem(id)}
              />
            ))}
        </div>
      </CustomTabPanel>
      <CustomTabPanel className="App-TabPanel" tabValue={tabValue} index={3}>
        <div className='App-Scheduler' ref={schedulerContainerRef} onScroll={handleSchedulerScroll}>

          <div className="App-SchedulerTaskTree" style={{paddingLeft: isMobile ? '10px':'32px'}}>
            <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
              <div ref={schedulerTreeDraggableContainerRef} style={{ width: '100%', height: '90%', minHeight: '66%' }}>
                <List
                  onScroll={handleTaskListScroll}
                  key={`list-length-${filteredFlattenedItems.length}`}
                  listRef={setListRef}
                  rowCount={filteredFlattenedItems.length}
                  rowHeight={(index) => getRowHeight(index)}
                  onRowsRendered={handleRowsRendered}
                  // scrollOffset={scrollOffset}
                  overscanCount={2}   // Pre-renders n rows off-screen for smooth scrolling
                  rowComponent={RenderRow}
                  rowProps={{
                    // isDraggable: false,
                    // indendationWidthOverride: 0
                  }}//{{}}
                  rowKey={(index) => getRowKey(index)}
                  style={{
                    width: '100%',
                    height: '100%', // Dictated completely by the style prop!
                    // contain: 'layout paint style'
                    }}
                  // defaultWidth={800}  
                  defaultHeight={1024} 
                  // useIsScrolling={true}
                />
              </div>

              {createPortal(
                <DragOverlay
                  dropAnimation={dropAnimationConfig}
                  modifiers={indicator ? [adjustTranslate] : undefined}
                >            
                  {activeId && activeItem ? (
                    <div className="fc-draggable-node"
                      data-id={activeItem.id}             // 👈 Stores the metadata in the DOM safely
                      data-title={activeItem.title}>
                      <SortableTreeItem
                        id={activeId}
                        depth={activeItem.depth}
                        clone
                        childCount={getChildCount(items, activeId) + 1}
                        value={activeItem.title}
                        indentationWidth={indentationWidth}
                      /></div>
                  ) : null}
                </DragOverlay>,
                document.body
              )}
            </SortableContext>
            <div className='App-SchedulerCalendarControls'>
              <Tooltip title='test' // TODO: Rename this.
               >
                <Reschedule onClick={rescheduleFilteredItemsToCalendarDay}/>
              </Tooltip>
            </div>
          </div>
          <div className="App-SchedulerCalendar">
            <FullCalendar
              // className="App-SchedulerCalendar"
              // ref={calendarRef}
              plugins={[ dayGridPlugin, multiMonthPlugin, timeGridPlugin, listPlugin, interactionPlugin ]}
              editable={true}
              eventResizableFromStart={true}
              droppable={true}
              eventColor={isPlanningMode? "#84baecff" : ""}
              initialView={isPlanningMode? calendarViewModes[activePlanningCalendarViewModeIndex].name : calendarViewModes[activeCalendarViewModeIndex].name}
              timeZone='local'
              initialDate={activeCalendarViewDisplayedDateRangeStartDateTime}
              height="100%"
              headerToolbar={{
                left: "today prev",
                center: "title",
                right: "next",
              }}
              footerToolbar={{
                // left: "today prev",
                // center: "title",
                // right: "next multiMonthYear,dayGridYear dayGridMonth,timeGridWeek,timeGridDay listMonth,listWeek,listDay"
                right: "multiMonthYear,dayGridYear dayGridMonth,timeGridWeek,timeGridDay"
              }}
              // height={665}
              // dayCount={1}
              nowIndicator={true}
              scrollTime={'11:00:00'}
              weekends={true}
              multiMonthMaxColumns={1}
              // defaultAllDay={isPlanningMode ? true : false}
              events={calendarFilteredFlattenedItems}
              eventChange={handleEventChangedInCalendar}
              viewClassNames={handleCalendarViewChange}
              datesSet={handleCalendarViewDatesSet}
              aspectRatio={1}
              handleWindowResize={1}
              // nowIndicatorClassNames="fc-now-indicator"
              />
          </div>
        </div>
      {isMobile && <div style={{minHeight:'140px', height: '140px', }}/>}
      </CustomTabPanel>
      <CustomTabPanel className="App-TabPanel" tabValue={tabValue} index={4}>
        {/* <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column',  hieght: '100%', width: '100%', minHeight: 0, minWidth: 0, overflow: 'hidden',
            position: 'relative',
            '& .fc': {
              height: '100% !important',
              width: '100% !important',
              display: 'flex !important',
              flexDirection: 'column !important',
              minWidth: 0,
            },
            '& .fc-view-harness': {
              flexGrow: 1,
              width: '100% !important',
              minHeight: 0
            },
            // '& .fc-header-toolbar': {
            //   padding: '12px 16px 4px 16px', // Safely spaces your calendar header buttons
            //   margin: '0 !important'
            // },
            // '& .fc-footer-toolbar': {
            //   padding: '12px 4px 16px 16px', // Safely spaces your calendar header buttons
            //   margin: '0 !important'
            // },
            // '& .fc-view': {
            //   // padding: '0 16px 16px 16px'  // Safely pads the main calendar grid canvas
            // }
         }}> */}
          <div className="App-Calendar">
            <FullCalendar
              // ref={calendarRef}
              plugins={[ dayGridPlugin, multiMonthPlugin, timeGridPlugin, listPlugin, interactionPlugin ]}
              editable={true}
              eventResizableFromStart={true}
              eventColor={isPlanningMode? "#84baecff" : ""}
              initialView={isPlanningMode? calendarViewModes[activePlanningCalendarViewModeIndex].name : calendarViewModes[activeCalendarViewModeIndex].name}
              timeZone='local'
              initialDate={activeCalendarViewDisplayedDateRangeStartDateTime}
              height="100%"
              headerToolbar={{
                left: "today prev",
                center: "title",
                right: "next",
              }}
              footerToolbar={{
                // left: "today prev",
                // center: "title",
                // right: "next multiMonthYear,dayGridYear dayGridMonth,timeGridWeek,timeGridDay listMonth,listWeek,listDay"
                right: "multiMonthYear,dayGridYear dayGridMonth,timeGridWeek,timeGridDay"
              }}
              nowIndicator={true}
              scrollTime={'11:00:00'}
              weekends={true}
              multiMonthMaxColumns={2}
              // defaultAllDay={isPlanningMode ? true : false}
              events={calendarFilteredFlattenedItems}
              eventChange={handleEventChangedInCalendar}
              viewClassNames={handleCalendarViewChange}
              datesSet={handleCalendarViewDatesSet}
              aspectRatio={1}
              handleWindowResize={1}
              // nowIndicatorClassNames="fc-now-indicator"
              />
          </div>
        {/* </Box> */}
          {isMobile && <Box sx={{minHeight:'152px', height: '152px', }}/>}
      </CustomTabPanel>
      {/* <CustomTabPanel tabValue={tabValue} index={4}>
      <div className="App-Calendar">
            <DnDCalendar
              localizer={localizer}
              events={filteredFlattenedItems}
              draggableAccessor={(event) => true}
              startAccessor="start"
              endAccessor="end"
            />
      </div>
      </CustomTabPanel> */}
      {/* <CustomTabPanel tabValue={tabValue} index={5}>
      <div className="App-Tree">
          <MyButton items={items} setItems={setItems} goal={true}/>
          <p/>
        <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
          {goalFlattenedItems.map(({id, title, children, collapsed, idealCompletionDate, latestPossibleCompletionDate, done, depth, goal}) => (
            <SortableTreeItem
              key={id}
              id={id}
              value={title ? title: id.toString()}
              inputRef={id === focusToTreeItemId ? titleInputRef : null}
              idealCompletionDate={idealCompletionDate}
              latestPossibleCompletionDate={latestPossibleCompletionDate}
              done={done}
              depth={id === activeId && projected ? projected.depth : depth}
              goal={goal}
              indentationWidth={indentationWidth}
              indicator={indicator}
              collapsed={Boolean(collapsed && children.length)}
              onCollapse={
                collapsible && children.length
                  ? () => handleCollapse(id)
                  : undefined
              }
              onDoneChanged={() => handleDone(id)}
              onGoalChanged={() => handleGoal(id)}
              insertNewItemAfter={() => insertNewItemAfter(id)}
              onIdealDateChanged={(newDate) => handleOnIdealDateChanged(id, newDate)}
              onLatestDateChanged={(newDate) => handleOnLatestDateChanged(id, newDate)}
              onPrimaryLabelChanged={(newPrimaryLabel) => handleOnPrimaryLabelChanged(id, newPrimaryLabel)}
              onStatusChanged={(newStatus) => handleOnStatusChanged(id, newStatus)}
              onTitleChanged={(newTitle, focussedValue) => handleOnTitleChanged(id, newTitle, focussedValue)}
              onRemove={removable ? () => handleRemove(id) : undefined}
            />
          ))}
          {createPortal(
            <DragOverlay
              dropAnimation={dropAnimationConfig}
              modifiers={indicator ? [adjustTranslate] : undefined}
            >            
              {activeId && activeItem ? (
                <SortableTreeItem
                  id={activeId}
                  depth={activeItem.depth}
                  clone
                  childCount={getChildCount(items, activeId) + 1}
                  value={activeItem.title}
                  indentationWidth={indentationWidth}
                />
              ) : null}
            </DragOverlay>,
            document.body
          )}
        </SortableContext>
      
      </div>
      </CustomTabPanel> */}
      </DndContext>
      <Box
        component="BottomTabControlPanel"
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'fixed',
          bottom: 65, // Shifts up on mobile to avoid overlapping your AppLayout BottomNav!
          // left: 0,
          // minWidth: 150,
          // width: 305,
          width: '80%',
          // maxWidth: 700,
          zIndex: 1050,
        }}
      >
        <div className="App-BottomTabControlPanel">
          <IconLabelTabs tabValue={tabValue} setTabValue={setTabValue} />
        </div>
      </Box>
    </>}
    </div>
  );

  function handleTaskExpansionChanged(taskId: UniqueIdentifier, isExpanded: boolean): void {

    console.log("are we expanding in tree? : "+taskId+' '+isExpanded);
    // const newExpandedIds = expandedIdsMap;
    // newExpandedIds[taskId] = isExpanded;
    setExpandedIds(    (prev) => ({
    ...prev,
    [taskId]: isExpanded}));
  }

  function getRowHeight(index: number): number {
    const item = filteredFlattenedItems[index];
    let rowHeight = (item.parentId === null && index != 0) ? 55 : 36;

    if(expandedIdsMap[item.id] === true) {
      rowHeight = 177;
    }
    else {
      rowHeight = (item.parentId === null && index != 0) ? 55 : 36;
    }

    return rowHeight;
  }

  function handleDragStart({active: {id: activeId}}: DragStartEvent) {
    setActiveId(activeId);
    setOverId(activeId);

    const activeItem = flattenedItems.find(({id}) => id === activeId);

    if (activeItem) {
      setCurrentPosition({
        parentId: activeItem.parentId,
        overId: activeId,
      });
    }

    document.body.style.setProperty('cursor', 'grabbing');
  }

  function handleDragMove({delta}: DragMoveEvent) {
    setOffsetLeft(delta.x);
  }

  function handleDragOver({over}: DragOverEvent) {
    setOverId(over?.id ?? null);
  }

  function handleDragEnd({active, over}: DragEndEvent) {
    resetState();

    if (projected && over) {
      const {depth, parentId} = projected;
      const clonedItems: FlattenedItem[] = JSON.parse(
        //TODO:: Some of these flattenTree calls can probably be visable only...
        JSON.stringify(flattenTreeAll(items)) 
      );
      const overIndex = clonedItems.findIndex(({id}) => id === over.id);
      const activeIndex = clonedItems.findIndex(({id}) => id === active.id);
      const activeTreeItem = clonedItems[activeIndex];

      clonedItems[activeIndex] = {...activeTreeItem, depth, parentId};

      const sortedItems = arrayMove(clonedItems, activeIndex, overIndex);
      const newItems = buildTree(sortedItems);

      updateItems(newItems);
      const relocatedItem = flattenTreeAll(newItems).find(({id}) => id === sortedItems[overIndex].id);
      if(relocatedItem && relocatedItem.parentId){
        const relocatedItemParent = flattenTreeAll(newItems).find(({id}) => id === relocatedItem.parentId);
        if(relocatedItemParent && relocatedItemParent['goal'] === true) {
          handleGoal(relocatedItem.id);
        }
      }
      saveTaskItemsToLocalStorage(newItems);
    }    
  }

  function handleDuplicateItem(itemToCloneiD: UniqueIdentifier) {
    console.log("is handling 'duplicate' working??");

      const itemToClone = findItemDeep(items, itemToCloneiD)//clonedItems[index];
      const clonedItemTree = structuredClone(itemToClone);
      if(itemToClone && clonedItemTree) {
        
        console.log("Did we clone? : " + clonedItemTree);
        setPropertyOnTree([clonedItemTree], clonedItemTree.id, 'id', (value) => {return uuid()})
        setPropertyOnTree([clonedItemTree], clonedItemTree.id, 'ownerId', (value) => {return getAuth().currentUser?.uid})
        setPropertyOnTree([clonedItemTree], clonedItemTree.id, 'creationDate', (value) => {return (new Date(Date.now())).toISOString()})
        
        const filterdFlattenedItemToClone = filteredFlattenedItems.find(({id}) => id === itemToCloneiD);

        console.log("Did we find the flattenedItem? : " + filterdFlattenedItemToClone);
        if(filterdFlattenedItemToClone) {
          console.log("Did we find the flattenedItem? : " + filterdFlattenedItemToClone);
          const parentId = filterdFlattenedItemToClone.parentId;

          console.log("Did we find the parentID of the flattened item to clone? : " + parentId);
          let newItems = items;

          if(parentId) {
            console.log("Are we duplicating a branch/child task tree? : " + parentId);
            newItems = [...items]
            let parentOfitemToClone = findItemDeep(items, parentId);
            if(parentOfitemToClone) {
              let childrenOfParentOfItemToClone = parentOfitemToClone.children;
              const index = childrenOfParentOfItemToClone.findIndex(({id}) => id === itemToCloneiD);
  
              console.log("Did we find the filteredItems index of the branch/child task tree? : " + index);
              if( index+1 < childrenOfParentOfItemToClone.length) {
                parentOfitemToClone.children = childrenOfParentOfItemToClone.slice(0,index+1).concat(clonedItemTree, childrenOfParentOfItemToClone.slice(index+1));
              }
              else {
                parentOfitemToClone.children = childrenOfParentOfItemToClone.slice(0,index+1).concat(clonedItemTree);
              }
            }

          }
          else {
            console.log("Are we duplicating a root task tree? : " + parentId);
              const index = items.findIndex(({id}) => id === itemToCloneiD);

              console.log("Did we find the filteredItems index of the root task tree? : " + index);
              if( index+1 < items.length) {
                newItems = items.slice(0,index+1).concat(clonedItemTree, items.slice(index+1));
              }
              else {
                newItems = items.slice(0,index+1).concat(clonedItemTree);
              }

          }

          if(newItems) {
            console.log("Setting items after duplicating? : " + newItems);
            updateItems(newItems);
            saveTaskItemsToLocalStorage(newItems);
          }

        }

    } 

  }

  function handleDragCancel() {
    resetState();
  }

  function resetState() {
    setOverId(null);
    setActiveId(null);
    setOffsetLeft(0);
    setCurrentPosition(null);

    document.body.style.setProperty('cursor', '');
  }

  function handleShowAllFilteredTasks() {
    setSelectedNavigationTaskId(null);
    setSelectedNavigationTaskParentFlattenedItem(null);
  }

  function handleShowParentBranch() {
    if(selectedNavigationTaskParentFlattenedItem && 
       selectedNavigationTaskParentFlattenedItem.parentId !== undefined) {
        handleNavToItem(selectedNavigationTaskParentFlattenedItem.id);
    }
  }

  function handleItemSelected(selectedItemId: UniqueIdentifier | null) {
    const localFlattenedTree = flattenTree(items);
    const selectedItem = localFlattenedTree.find(({id}) => id === selectedItemId);
    // if(currentNavTask !== undefined) {
    //   const parentNavTask = localFlattenedTree.find(({id}) => id === currentNavTask.parentId);
      console.log("Show Selected item ID: " + selectedItem.title);
      // console.log("Show Nav parent ID: " + currentNavTask.parentId);
    //   if(parentNavTask !== undefined) {
    //     setSelectedNavigationTaskParentFlattenedItem(parentNavTask);
    //   }
    // }
    // setSelectedItem &&
     setSelectedItem(selectedItem);
  }

  function handleNavToItem(newId: UniqueIdentifier | null) {
    const localFlattenedTree = flattenTree(items);
    const currentNavTask = localFlattenedTree.find(({id}) => id === newId);
    if(currentNavTask !== undefined) {
      const parentNavTask = localFlattenedTree.find(({id}) => id === currentNavTask.parentId);
      console.log("Show Nav item ID: " + currentNavTask.title);
      console.log("Show Nav parent ID: " + currentNavTask.parentId);
      if(parentNavTask !== undefined) {
        setSelectedNavigationTaskParentFlattenedItem(parentNavTask);
      }
      else {
        setSelectedNavigationTaskParentFlattenedItem(null);
      }
    }
    setSelectedNavigationTaskId(newId);
  }

  // function handleDuplicate(id: UniqueIdentifier) {
  //   const newItems = removeItem(items, id);
  //   setItems((items) => removeItem(items, id));
  //   saveTaskItemsToLocalStorage(newItems);
  // }

  function handleRemove(id: UniqueIdentifier) {
    const newItems = removeItem(items, id);
    updateItems((items) => removeItem(items, id));
    saveTaskItemsToLocalStorage(newItems);
  }

  function handleAdd(id: UniqueIdentifier) {
    updateItems((items) => addItem(items, id));
  }

  function handleDone(id: UniqueIdentifier) {
    console.log("is handling 'done' working??");
    updateItems((items) =>
      setProperty(items, id, 'done', (value) => {
        console.log("is setting 'done' working?? "+id+' '+value+' '+!value);
        return !value;
      })
    );
    saveTaskItemsToLocalStorage(items);
    console.log("Handling 'done' complete.");
  }

  function handleGoal(thisId: UniqueIdentifier) {
    console.log("is handling 'goal' working??");

    const thisItem = flattenTree(items).find(({id}) => id === thisId);
    if(thisItem){
      console.log("thisItem parentId: "+ thisItem.parentId);
      let parentId;
      if(thisItem.parentId){
        parentId= thisItem.parentId;
      }
      else {
        parentId= ''; //FIXME!! super hacky!!
      }
      const parentItem = findItem(items, parentId); 
        
      console.log("parentItem: "+ parentItem);
      console.log("thisItem: "+ thisItem.id.toString());
      if(parentItem){
        console.log("parentItem: "+ parentItem.id.toString());
      }
      else {
        console.log("parentItem: NULL/UNDEFINED!!!");
      }

      updateItems((items) =>
        setGoalPropertyOnTree(items, thisId, 'goal', 
          (value) => {
            console.log("is setting 'goal' working?? "+thisId+' '+value+' '+!value);
            return !value;
          }, 
          parentItem)
      );
      saveTaskItemsToLocalStorage(items);
    }
    //TODO: Update the item's tree position if it is set to a goal status that is different to its parent's.
    // if(item['goal'] == 'true'){

    // }
    console.log("Handling 'goal' complete.");
  }

  function areDatesMoreThanOneDayApart(date1, date2) {
  // Ensure both inputs are valid Date objects
  if (!(date1 instanceof Date) || !(date2 instanceof Date)) {
    throw new Error("Inputs must be valid Date objects.");
  }

  // Calculate the difference in milliseconds
  const diffInMilliseconds = Math.abs(date1.getTime() - date2.getTime());

  // Convert milliseconds to days (1 day = 24 hours * 60 minutes * 60 seconds * 1000 milliseconds)
  const oneDayInMilliseconds = 24 * 60 * 60 * 1000;
  const diffInDays = diffInMilliseconds / oneDayInMilliseconds;

  // Return true if the difference is greater than 1 day
  return diffInDays > 1;
  }

    function handleOnEstimatedCommencementDateChanged(thisId: UniqueIdentifier, newDateTime: string) {
    console.log("is handling 'onEstimatedCommencementDateChanged' working??");
    updateItems((items) =>
      setProperty(items, thisId, 'estimatedCommencementDate', (aDate) => {//TODO: WTF is going on here?!...
        console.log("is setting 'estimatedCommencementDate' working?? "+thisId+' '+newDateTime);

        setProperty(items, thisId, 'start',  (oldDate) => {//TODO: WTF is going on here?!...
          const newScheduledStartDate = new Date((new Date(newDateTime)).toUTCString());
          console.log("is setting 'start' working?? "+thisId+' '+new Date(newDateTime+'Z'));
          return newScheduledStartDate;

          });

        // const thisItem = flattenTree(items).find(({id}) => id === thisId);
        //   if(areDatesMoreThanOneDayApart(new Date(thisItem['start']), new Date(thisItem['end']) )) {
                    // setProperty(items, thisId, 'allDay',  () => {
                    //     console.log("is setting 'allDay' working?? "+thisId);
                    //     return true;
                    // });
        //   }
        //   else {
        //       setProperty(items, thisId, 'allDay',  () => {
        //         console.log("is setting 'allDay' working?? "+thisId);
        //         return false;
        //       });
        //   }
        return newDateTime;
      })
    );
    saveTaskItemsToLocalStorage(items);
    console.log("What's the time MOFO!"+newDateTime);
    // console.log("What's the time MOFO!"+newScheduledStartDate);
    console.log("Handling 'onEstimatedCommencementDateChanged' complete.");
  }

  function handleOnIdealDateChanged(thisId: UniqueIdentifier, newDateTime: string) {
    console.log("is handling 'onIdealDateChanged' working??");
    updateItems((items) =>
      setProperty(items, thisId, 'idealCompletionDate', (aDate) => {//TODO: WTF is going on here?!...
        console.log("is setting 'idealCompletionDate' working?? "+thisId+' '+newDateTime);

        setProperty(items, thisId, 'start',  (oldDate) => {//TODO: WTF is going on here?!...
          const newScheduledStartDate = new Date((new Date(newDateTime)).toUTCString());
          console.log("is setting 'start' working?? "+thisId+' '+new Date(newDateTime+'Z'));
          return newScheduledStartDate;

          });

          const thisItem = flattenTreeAll(items).find(({id}) => id === thisId);
          if(areDatesMoreThanOneDayApart(new Date(thisItem['start']), new Date(thisItem['end']) )) {
                    setProperty(items, thisId, 'allDay',  () => {
                        console.log("is setting 'allDay' working?? "+thisId);
                        return true;
                    });
          }
          else {
              setProperty(items, thisId, 'allDay',  () => {
                console.log("is setting 'allDay' working?? "+thisId);
                return false;
              });
          }
        return newDateTime;
      })
    );
    saveTaskItemsToLocalStorage(items);
    console.log("What's the time MOFO!"+newDateTime);
    // console.log("What's the time MOFO!"+newScheduledStartDate);
    console.log("Handling 'onIdealDateChanged' complete.");
  }

  function handleOnLatestDateChanged(thisId: UniqueIdentifier, newDateTime: string) {
    console.log("is handling 'onLatestDateChanged' working??");
    updateItems((items) =>
      setProperty(items, thisId, 'latestPossibleCompletionDate', (aDate) => {
        console.log("is setting 'latestPossibleCompletionDate' working?? "+thisId+' '+newDateTime);

        setProperty(items, thisId, 'end',  (oldDate) => {//TODO: WTF is going on here?!...
          const newScheduledEndDate = new Date((new Date(newDateTime)).toUTCString());
          console.log("is setting 'end' working?? "+thisId+' '+new Date(newDateTime+'Z'));
          return newScheduledEndDate;

          });
          
          const thisItem = flattenTreeAll(items).find(({id}) => id === thisId);
          if(areDatesMoreThanOneDayApart(new Date(thisItem['start']), new Date(thisItem['end']) )) {
                    setProperty(items, thisId, 'allDay',  () => {
                      console.log("is setting 'allDay' working?? "+thisId);
                      return true;
                    });
          }
          else {
              setProperty(items, thisId, 'allDay',  () => {
                console.log("is setting 'allDay' working?? "+thisId);
                return false;
              });
          }
        return newDateTime;
      })
    );
    saveTaskItemsToLocalStorage(items);
    console.log("Handling 'onLatestDateChanged' complete.");
  }

  function handleOnPrimaryLabelChanged(id: UniqueIdentifier, newPrimaryLabel: string) {
    console.log("is handling 'onPrimaryLabelChanged' working??");
    updateItems((items) =>
      setProperty(items, id, 'primaryLabel', () => {
        console.log("is setting 'primaryLabel' working?? "+id+' '+newPrimaryLabel);
          return newPrimaryLabel;
      })
    );
    saveTaskItemsToLocalStorage(items);
    console.log("Handling 'onPrimaryLabelChanged' complete.");
  }

  function handleOnStatusChanged(id: UniqueIdentifier, newStatus: string) {
    console.log("is handling 'onStatusChanged' working??");
    updateItems((items) =>
      setProperty(items, id, 'status', () => {
        console.log("is setting 'status' working?? "+id+' '+newStatus);
          return newStatus;
      })
    );
    saveTaskItemsToLocalStorage(items);
    console.log("Handling 'onStatusChanged' complete.");
  }

  function handleOnTitleChanged(changedId: UniqueIdentifier, newTitle: string) {

    console.log("Handling title change.");
    //TODO: MUUUUUUUCH optimisation needed here...
    if(filteredFlattenedItems.find(({id}) => id === changedId)['title'] === newTitle) {
      console.log("ID is actually matching on title change!!");
      
    }
    else {

      console.log("ID not matched on title change!!");

      updateItems((items) =>
        setProperty(items, changedId, 'title', (aDate) => {
          console.log("is setting 'title' working?? "+changedId+' '+newTitle);
            return newTitle;
        })
      );

      saveTaskItemsToLocalStorage(items);

    };
  }

  function handleOnUpArrowPressed(changedId: UniqueIdentifier) {

    const flattenedItem = filteredFlattenedItems.find(({id}) => id === changedId);
    if(flattenedItem !== undefined) {
      const flattenedItemIndex = filteredFlattenedItems.indexOf(flattenedItem);
      if(flattenedItemIndex > -1) {
        //TODO: Refactor        
        const aboveFilteredFlattenedItem = filteredFlattenedItems[
          (flattenedItemIndex > 0) ? flattenedItemIndex-1:0
        ];
        focusToTreeItemIdRef.current=aboveFilteredFlattenedItem['id']
      }
    };
  }

  function handleOnDownArrowPressed(changedId: UniqueIdentifier) {

    const flattenedItem = filteredFlattenedItems.find(({id}) => id === changedId);
    if(flattenedItem !== undefined) {
      const flattenedItemIndex = filteredFlattenedItems.indexOf(flattenedItem);
      if(flattenedItemIndex > -1) {
        //TODO Rrfactor.
        const belowFilteredFlattenedItem = filteredFlattenedItems[
          (flattenedItemIndex != (filteredFlattenedItems.length-1)) ? 
          flattenedItemIndex+1:
          flattenedItemIndex
        ];
        focusToTreeItemIdRef.current=belowFilteredFlattenedItem['id']
      }
    };
  }

  function handleCollapse(id: UniqueIdentifier) {
    updateItems((items) =>
      setProperty(items, id, 'collapsed', (value) => {
        console.log('is collapse working?? '+value+' '+!value);
        return !value;
      })
    );
  }

  function handleEventChangedInCalendar(eventResizeInfo){

    const updatedEvent = eventResizeInfo.event;
    console.log("This is the new start date/time string received from the calendar: "+  updatedEvent.startStr);

  let startString = "";
  let endString = "";

// 
  // if (initialEarliestEstimatedCommencementDateTime !== "") {
    const startTimeMs = updatedEvent.start.getTime();
    const utcDate = new Date(startTimeMs);
    const localTime = startTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(localTime);
    startString = localDate.toISOString().replace("Z", "");
    console.log(utcDate.toISOString(), startString);
  // }

    const endTimeMs = updatedEvent.end.getTime();
    const utcDate2 = new Date(endTimeMs);
    const localTime2 = endTimeMs - utcDate2.getTimezoneOffset() * 60 * 1000;
    const localDate2 = new Date(localTime2);
    endString = localDate2.toISOString().replace("Z", "");
    console.log(utcDate.toISOString(), endString);


    if(isPlanningMode) {

      // if(updatedEvent.allDay) {
      //   handleOnEstimatedCommencementDateChanged(updatedEvent.id, updatedEvent.startStr);
      // }
      // else {
      //   handleOnEstimatedCommencementDateChanged(updatedEvent.id, updatedEvent.startStr.slice(0,-6));
      // }
      handleOnEstimatedCommencementDateChanged(updatedEvent.id, startString);
      // handleOnEstimatedCommencementDateChanged(updatedEvent.id, updatedEvent.startStr.slice(0,-6));
    }
    else {
      handleOnIdealDateChanged(updatedEvent.id, startString);
      // if(updatedEvent.allDay) {
      //   handleOnIdealDateChanged(updatedEvent.id, updatedEvent.startStr);
      // }
      // else {
      //   handleOnIdealDateChanged(updatedEvent.id, updatedEvent.startStr.slice(0,-6));
      // }
    }
    console.log("This is the new end date/time string received from the calendar: "+  updatedEvent.endStr);
    handleOnLatestDateChanged(updatedEvent.id, endString);
  }

  function handleCalendarViewChange(mountedViewInfo) {
    const viewType = mountedViewInfo.view.type;
    console.log("This is the new view type received from the calendar: "+  viewType);
    let calendarViewModeIndex = calendarViewModes.findIndex(({name}) => name === viewType);
    console.log("Could we match the view type with and internal string?: "+  calendarViewModeIndex);
    if(calendarViewModeIndex >= 0) {

      // const viewTitle = mountedViewInfo.view.title;

      // if(calendarViewModeIndex === 3 || calendarViewModeIndex === 4) {
      //   if(viewTitle.includes("-")) {
      //     calendarViewModeIndex = 3;
      //   }
      // }
      // else if(calendarViewModeIndex === 5 || 
      //         calendarViewModeIndex === 6 || 
      //         calendarViewModeIndex === 7) {
      //   if(viewTitle.includes("-")) {
      //     calendarViewModeIndex = 6;
      //   }
      //   else if(viewTitle.includes(",")){
      //     calendarViewModeIndex = 7;
      //   }
      // }
      if(isPlanningMode) {
        setActivePlanningCalendarViewModeIndex(calendarViewModeIndex);
      }
      else {
        setActiveCalendarViewModeIndex(calendarViewModeIndex);
      }
      
    }
  }

  function handleCalendarViewDatesSet(datesSetInfo) {
    const viewCurrentStart = datesSetInfo.view.currentStart.toISOString();
    // const viewCurrentEnd = datesSetInfo.view.currentEnd.toISOString();
    console.log("This is the start date/time received from the calendar: "+  viewCurrentStart);
    // console.log("This is the end date/time received from the calendar: "+  viewCurrentEnd);
    
    if(datesSetInfo.view.type !== calendarViewModes[0].name &&
       datesSetInfo.view.type !== calendarViewModes[1].name)
    {
      setActiveCalendarViewDisplayedDateRangeStartDateTime(viewCurrentStart);
    }
  }

  function rescheduleFilteredItemsToCalendarDay() {

    let tomorrow = new Date();
    if(activeCalendarViewDisplayedDateRangeStartDateTime){
      tomorrow = new Date(activeCalendarViewDisplayedDateRangeStartDateTime);
    }
    else {
      tomorrow.setDate(new Date().getDate() + 1);
    }    
    tomorrow.setHours(9, 0, 0); // Sets time to 09:00:00.000

    if(isPlanningMode) {

    }
    else {
      const startTimeMs = tomorrow.getTime();
      const utcDate = new Date(startTimeMs);
      const localTime = startTimeMs - utcDate.getTimezoneOffset() * 60 * 1000;
      const localDate = new Date(localTime);
      
      tomorrow.setHours(9,30,0)      
      const endTimeMs = tomorrow.getTime();
      const utcDate2 = new Date(endTimeMs);
      const localTime2 = endTimeMs - utcDate2.getTimezoneOffset() * 60 * 1000;
      const localDate2 = new Date(localTime2);
      
      for (const item of filteredFlattenedItems) {
        let startTimeString = localDate.toISOString().replace("Z", "");
        let endTimeString = localDate2.toISOString().replace("Z", "");
        handleOnIdealDateChanged(item.id, startTimeString);
        handleOnLatestDateChanged(item.id, endTimeString);
        localDate.setHours(localDate.getHours()+1);
        localDate2.setHours(localDate2.getHours()+1);        
      }
    }

  }

  function getMovementAnnouncement(
    eventName: string,
    activeId: UniqueIdentifier,
    overId?: UniqueIdentifier
  ) {
    if (overId && projected) {
      if (eventName !== 'onDragEnd') {
        if (
          currentPosition &&
          projected.parentId === currentPosition.parentId &&
          overId === currentPosition.overId
        ) {
          return;
        } else {
          setCurrentPosition({
            parentId: projected.parentId,
            overId,
          });
        }
      }

      const clonedItems: FlattenedItem[] = JSON.parse(
        JSON.stringify(flattenTree(items))
      );
      const overIndex = clonedItems.findIndex(({id}) => id === overId);
      const activeIndex = clonedItems.findIndex(({id}) => id === activeId);
      const sortedItems = arrayMove(clonedItems, activeIndex, overIndex);

      const previousItem = sortedItems[overIndex - 1];

      let announcement;
      const movedVerb = eventName === 'onDragEnd' ? 'dropped' : 'moved';
      const nestedVerb = eventName === 'onDragEnd' ? 'dropped' : 'nested';

      if (!previousItem) {
        const nextItem = sortedItems[overIndex + 1];
        announcement = `${activeId} was ${movedVerb} before ${nextItem.id}.`;
      } else {
        if (projected.depth > previousItem.depth) {
          announcement = `${activeId} was ${nestedVerb} under ${previousItem.id}.`;
        } else {
          let previousSibling: FlattenedItem | undefined = previousItem;
          while (previousSibling && projected.depth < previousSibling.depth) {
            const parentId: UniqueIdentifier | null = previousSibling.parentId;
            previousSibling = sortedItems.find(({id}) => id === parentId);
          }

          if (previousSibling) {
            announcement = `${activeId} was ${movedVerb} after ${previousSibling.id}.`;
          }
        }
      }

      return announcement;
    }

    return;
  }

  function insertNewItemAfter(activeId: UniqueIdentifier | null, ctrlCreate: boolean) {
    const cumulativeItemCount = parseInt(localStorage.getItem("itemCount"));
    let cumulativeItemCountString;
    console.log('Cumulative item count as parsed: '+cumulativeItemCount);
    if(isNaN(cumulativeItemCount)) {
      cumulativeItemCountString = "0";
    }
    else {
    cumulativeItemCountString = cumulativeItemCount;
    }

    // localStorage.setItem("itemCount", cumulativeItemCountString+1);
  
    if(items) {

      const newItem = [{id: uuid(), 
                        title: "Task #"+cumulativeItemCountString+" : ",
                        ownerId: getAuth().currentUser?.uid,
                        creationDate: new Date((new Date(Date.now())).toUTCString()),
                        children: [],
                        goal: (selectedTaskTier && selectedTaskTier.name === taskTiers[2].name) ? true: false, 
                        status: taskStatuses[0].name, 
                        primaryLabel: selectedPrimaryLifeArea.name === primaryLifeAreas[10].name ? primaryLifeAreas[0].name : selectedPrimaryLifeArea.name
                      }];
      console.log('About to set activeTreeItemId!!!! :' + newItem['id'] + ' ' + newItem['title']);
      
      const newItems = newItem.concat(items);
      console.log('About to set activeTreeItemId!!!! :' + newItems[0]['id'] + ' ' + newItems[0]['title']);

      handleOnFocus(newItems[0]['id']);

      const clonedItems: FlattenedItem[] = JSON.parse(
        JSON.stringify(flattenTreeAll(newItems))
      );
      const overIndex = clonedItems.findIndex(({id}) => id === activeId);
      const newItemIndex = 0;
      const newTreeItem = clonedItems[newItemIndex];
      const activeTreeItem = clonedItems[overIndex];
      
      let depth;
      let parentId;
      console.log("ctrlCreate: "+ ctrlCreate);
      if(ctrlCreate === true){
        depth = activeTreeItem['depth']+1;
        parentId = activeTreeItem['id'];
      }
      else {
        depth = activeTreeItem['depth']+1;
        parentId = activeTreeItem['parentId'];
      }
      
      const goal = activeTreeItem['goal'];
      const primaryLabel = activeTreeItem['primaryLabel'];

      clonedItems[newItemIndex] = {...newTreeItem, depth, parentId, goal, primaryLabel};

      const sortedItems = arrayMove(clonedItems, newItemIndex, overIndex);
      const newerItems = buildTree(sortedItems);

      updateItems(newerItems);

      saveTaskItemsToLocalStorage(newerItems);
    
      // const dbOpenRequest = window.indexedDB.open("TasksDB",1);
  
      // let db;
  
      // let objectStore;
  
      // dbOpenRequest.onerror = (event) => {
      //   console.error(`Database error: ${event.target.error?.message}`);
      //   alert("Issue connecting to DB!!");
      // };
  
      // dbOpenRequest.onsuccess = (event) => {
      //   db = event.target.result;
      //   console.log('DB connection opened for updating!!');
  
      // // This assumes the objectStore has already been successfully created in the DB
      // // TODO: handle the exception if not.
      // const objectStore = db
      //   .transaction("TaskNestedLists", "readwrite")
      //   .objectStore("TaskNestedLists");
        
      //   console.log('Got object store on button clicked!! '+ objectStore);
      //   const storedItems = event.target.result;
      //   console.log('Items obtained from DB: '+storedItems);
      //   const requestUpdate = objectStore.put(newItems,1);
      //   requestUpdate.onsuccess = (event) => {
      //     // Success - the data is updated!
      //   };
  
      // };
  
      // dbOpenRequest.onupgradeneeded = (event) => {
      //   db = event.target.result;
      //   alert("DB was upgraded!!");  
        
      //   const objectStore = db
      //   .transaction("TaskNestedLists", "readwrite")
      //   .objectStore("TaskNestedLists");
  
      //   objectStore.put(newItems, 1);
  
      // };
  
    }
  }
}

const adjustTranslate: Modifier = ({transform}) => {
  return {
    ...transform,
    y: transform.y - 25,
  };
};

